/**
 * Wall — the page component (plan d § Files). Header search bound to `?q=`, justified rows,
 * windowing, the ghost first, tile doors, the armed column (desktop) / sheet (phone), the
 * pill, `/mine` polling, `/seen` stamping, and the sign-in overlay with the return-token re-arm.
 * `HouseTheme` carries what varies per house (BRIEF: "components live in `@core/wall/ui/` with a
 * `HouseTheme` prop"); only Toile is wired by this plan (Narcis/Ovid are plans f/g), so `header` and
 * `signIn` are render props the product route supplies rather than something this file imports —
 * `@core/*` must not reach into `@product/*`.
 *
 * ONE SOURCE OF TRUTH (the adversarial review's blocking finding B1). This component holds three
 * things and nothing else about the work: the armed CHAIN (`Armed`, keyed on `root_tile_id` or
 * `root_id` — `chains.ts`), the direction draft, and the ratio pick. Every value on screen is
 * derived per render from `GET /mine`: the column's basis and result, whether a cell shimmers, the
 * pill's slot and its line, what a Generate posts, what the return token carries. Nothing is
 * frozen when a tile is armed, so a take that finishes while the column is open replaces the
 * picture in the column and in the pill in the same frame, from the same value. The one held thing
 * is WHERE cells sit: the order is placed from a snapshot of `/mine` taken at an entry (`Hold`), so
 * a take lands in the cell the user acted on and the wall reorders only when the user crosses a
 * threshold.
 *
 * The URL is a MIRROR, never an input: `q` and the return token are written with
 * `history.replaceState`, so a keystroke never re-runs the route loader (review item S12 — the
 * loader reads the wall's first page and `/mine` on every run).
 */
import {useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState} from 'react'
import {useLocation, useSearchParams} from 'react-router'
import {track} from '@core/utils/analytics'
import {useConsentGate, type ConsentCardProps} from '@core/consent/useConsentGate'
import {
    MAX_DIRECTION_CHARS, MAX_SEEN_IDS, RATIO_KEYS, RETURN_TOKEN_KEYS,
    GATE_TRIGGER_WALL, GATE_TRIGGER_SHARE, isTake,
    type TileSummary, type WallResponse, type MineResponse, type RatioKey, type Take, type Identity,
    type AnonTile, type AnonWallResponse,
    type ContinueResponse, type ErrorResponse, type SeenResponse, type WallArmedFrom, type WallChipTappedFrom, type WallMakeVia, type WallSearchFrom, type WallWordTappedFrom,
    type ChainResponse, type PinResponse,
} from '@core/wall/contract'
import {chainRootPath} from '@core/wall/chainRoot'
import type {FacetOffer} from '@core/wall/facetOffer'
import {mintRequestId} from '@core/wall/requestId'
import {writeWallWidthCookie} from '@core/wall/ssrWidth'
import {useWallPages} from './useWallPages'
import {useSettledQuery} from './useSettledQuery'
import {SwapFade, fadeRects, useSwapFade} from './SwapFade'
import {answeredQuery as fieldAnswer} from './answerPause'
import {createTouchQueue, readTouchAnswer, type TouchBody} from './touchQueue'
import {useAnonPage} from './useAnonPage'
import {makeToArm, searchToReport} from './searchReport'
import {FACET_SHARES, isHouseFacet} from '@core/wall/houseFacets'
import type {HeaderSearchSlot} from './Header'
import type {SearchFrom, SearchSteer} from './HeaderSearch'
import {browserStore, pushRecent, readRecent, removeRecent, writeRecent} from './recentSearches'
import {droppedLine, leanOf, makeDirection, matchedOnScreen, withThese, withoutToken, withToken, yoursOf, type FieldToken} from './searchModel'
import {readWallReturn, sessionStore, writeWallReturn} from './wallReturn'
import {useMine} from './useMine'
import {useOwnByTile} from './useOwnByTile'
import {boundedRows, justifyRows} from './justify'
import {composeQuery, parseQuery, readWallQueryParam, samePhrase, withWallQuery} from '@core/wall/tsQuery'
import {
    GHOST_CHAIN_KEY, armedAspect, armedChainKey, armedChainRoot, armedBasis, armedTileId, batchCover, batchTakes,
    type Door, buildChains, captionDoors, chainHistory, chainKeyOf, chainSteps, collapseChains, continueBody, displayedStep,
    allowanceRefusal, armAfterQueuedMuse, gateMet, effectiveRatio, errorLineFor, failedLineFor, holdTakes, identityArmable, isDuplicatePress,
    justTurnedDone, newestOwnRow, noteForOverlay,
    pillContinueAction, pressKeyOf, recordTakes, returnTokenFor, rowInFlight, scratchChainKey, shownStep,
    takeGenerateKind, tileChainKey, unseenDone,
    verbLabel, holdVerb, wallIdentity, wordTapFolds,
    type Armed, type ChainKey, type ChainStep, type Press, type TakeLedger, type VerbState,
} from './chains'
import {strippedSearch} from '@core/wall/pageVerb'
import {anonCells, bornTakes, cellKeyForChain, wallCells, withoutUnpinned, type Born} from './cells'
import {Tile} from './Tile'
import {usePreviewFrames} from './usePreviewFrames'
import {Ghost} from './Ghost'
import {Column} from './Column'
import type {PinSlotState} from './Counter'
import {takePicture, tilePicture, useShare} from './useShare'
import {freePickOpen, ownsRows} from './freePick'
import {Pill, type PillProps} from './Pill'
import {Sheet} from './Sheet'
import {useFocusTrap} from './useFocusTrap'
import {isSheetSurface} from './surface'
import {chromeWords, ghostWords, type HouseTheme} from './theme'
import styles from './Wall.module.scss'

const SCROLL_RETURN_THRESHOLD = 48
/** How long the arrival set must have been painted at rest before `/seen` stamps it. */
const ARRIVAL_PAINT_MS = 1200
/** How long a one-off line (`failed`, a refused `/continue`) stays on the pill. Said once. */
const NOTICE_MS = 8_000
/** `setTimeout` keeps a 32-bit delay; a longer one fires at once. */
const MAX_TIMER_MS = 2 ** 31 - 1
/** The line the plan names verbatim for a `POST /pin` that did not land ("revert and show the
 *  standard `warning` notice line"), whatever the actual status — a pin is a low-stakes toggle, not
 *  a render, so it never earns `errorLineFor`'s finer-grained vocabulary. Already in `chains.ts`'s
 *  `NOTICE_WARNING_LINES`, so `noticeKindFor` reads it as a warning with no change there. */
const PIN_ERROR_LINE = 'try again in a minute'
const PILL_DISMISSED_KEY = 'wall_pill_dismissed_at'
/**
 * What the server assumes about the viewport, and what the FIRST client render must assume with it
 * (re-review N3): windowing that read `window.innerHeight` on the hydrating render chose a spacer
 * where the server had chosen a row on every viewport but 900px tall, and React threw the whole SSR
 * subtree away. Both constants are replaced by measurements in `useIsomorphicLayoutEffect` below —
 * after hydration, before the browser paints, so the wall justifies once at its true width.
 */
const SSR_VIEWPORT_H = 900
/** `.wall` (Wall.module.scss) has no max-width, so no width is knowable server-side; this is the
 *  desktop assumption the layout is drawn at, and the measurement lands before first paint. */
const SSR_CONTAINER_WIDTH = 1200

/** What `useWallPages` is seeded with when the house serves a visitor its own finite page instead
 *  of a catalog (`anonPage` below): no tiles to page through and no cursor, so `hasMore` is false
 *  from the first render and the bottom sentinel's observer never fetches anything. */
const NO_WALL_PAGE: WallResponse = {tiles: [], next_cursor: null, seam: false}

/**
 * The wall's placement input, taken from `/mine` at an entry and held until the next (plan
 * `2026_09_26_wall_hold_still_pill.md` § The rule) — `key` names the entry it was taken at. `born`
 * grows within the entry (`cells.bornTakes`) and empties at the next one, which places those rows by
 * their day like any other.
 */
type Hold = {
    key: string
    now: number
    ownDone: readonly Take[]
    ownMatched: readonly Take[]
    ownPinned: readonly Take[]
    batch: readonly Take[]
    born: readonly Born[]
}

/** Layout measurement must beat the paint on the client; on the server there is nothing to measure
 *  and `useLayoutEffect` would only warn. */
const useIsomorphicLayoutEffect = typeof window === 'undefined' ? useEffect : useLayoutEffect

export type WallProps = {
    theme: HouseTheme
    /** The catalog's first page, server-rendered. Omitted only by a closed house's visitor branch,
     *  which hands `anonPage` instead — there is no catalog to page through there. */
    initialWallPage?: WallResponse
    /** The reader's profile key as the document was answered with (`profile.ts`); the touches this page makes move it. */
    profile?: string
    /** The search panel's facet groups ranked for this visitor and day, as the document was answered with. */
    facetOffer?: FacetOffer
    /** A closed house's finite visitor page (plan `2026_09_17_ovid_one_wall.md` § Decided 1): the
     *  whole wall, in one response, of cells that carry no estate text by type. Present only
     *  alongside `theme.visitor` and `signedIn: false`; the wall is otherwise exactly the same
     *  component, with the same cells, layout, doors, sign-in layer and overlays. */
    anonPage?: AnonWallResponse
    initialMine: MineResponse | null
    signedIn: boolean
    /** The container width the server justified at (`@core/wall/ssrWidth`, the reader's last
     *  measurement). Comes through the loader, never read on the client, so both renders start
     *  from one number; absent, `SSR_CONTAINER_WIDTH`'s guess. */
    ssrContainerWidth?: number | null
    /** `signIn` opens the wall's own sign-in layer — the same one a visitor's tile and the ghost's
     *  door open — so a house header can carry a Sign in door without holding that state itself.
     *  `null` once the reader is signed in. */
    renderHeader: (search: HeaderSearchSlot, signIn: (() => void) | null) => React.ReactNode
    /** `hasPicture`: a visitor holding a free take (`HouseTheme.freePick`) - the card may speak of it. */
    renderSignIn: (props: {redirectTo: string; trigger: string; hasPicture: boolean; onClose: () => void}) => React.ReactNode
    /** The terms gate's card (plan 2026_09_01_tos_gate.md §8.4) — a render prop for the same reason
     *  `renderHeader`/`renderSignIn` are: `@core/*` must not reach into `@product/*` for the styles,
     *  copy and terms/privacy hrefs a product's own `TosConsentCard` instance carries. The full
     *  `ConsentCardProps` (not a hand-picked subset) — C1 (2026-09-01 fix round): the earlier
     *  `{checked, onTick}` subset silently dropped `focusToken`, which is why the wall's Column/Sheet
     *  never re-focused the checkbox on a re-press while narcis's and ovid's own CTA slots and the
     *  toile stage already wired it correctly. */
    renderConsentCard: (props: ConsentCardProps) => React.ReactNode
    /** The wall's last element, below the rows and inside the page (plan § Decided 5 — at Ovid, the visitor's
     *  ending over the coda). `openSignIn` opens the visitor sign-in layer. Nothing at Toile, whose wall has no end. */
    footer?: React.ReactNode | ((q: string, openSignIn: () => void) => React.ReactNode)
    /** A line the house says once when a reader comes back signed in from a choice made before the card (Ovid's
     *  muse): it is put on the armed tile's chain, or on the ghost's. */
    arrivalNotice?: string | null
    /** The model-onboarding overlay (Narcis's `ModelIntake`), a render prop like `renderSignIn`.
     *  Opened by the verb while `identity` is `{kind: 'model', state: 'missing'}`; unset at a house
     *  with no model to train. `onClose({queued: true})` flips the bar to training at once, ahead
     *  of the `/mine` poll that fills thumb and eta. `reopenPath` is the address that opens it
     *  again on the armed tile — where a sign-in or a payment asked mid-intake returns. */
    renderIdentityIntake?: (props: {onClose: (result?: {queued: true}) => void; reopenPath: string | null}) => React.ReactNode
}

function tileLabel(tile: TileSummary): string {
    return tile.chips.length > 0 ? `${tile.title} · ${tile.chips.join(' · ')}` : tile.title
}

/**
 * The width a row may occupy: `.wall`'s CONTENT box. `clientWidth` includes padding (`.wall` pads
 * 18/22, Wall.module.scss), so justifying to it made every row 44px wider than the box that holds
 * them — the wall ran past the right edge at every zoom, and quies's unscoped `html/body {
 * overflow-x: hidden}` (products/quies/styles/index.scss:83,88, in the shared bundle) turned the
 * overflow into a clean cut rather than a scrollbar (re-review N17). Floored: a fractional
 * container plus per-row rounding is the other way a row ends up a pixel too wide.
 */
function contentWidth(el: HTMLElement): number {
    const style = window.getComputedStyle(el)
    const padding = (parseFloat(style.paddingLeft) || 0) + (parseFloat(style.paddingRight) || 0)
    return Math.max(0, Math.floor(el.clientWidth - padding))
}

function replaceUrl(params: URLSearchParams) {
    const qs = params.toString()
    const url = `${window.location.pathname}${qs ? `?${qs}` : ''}${window.location.hash}`
    window.history.replaceState(window.history.state, '', url)
}

function basisLabel(armed: Armed, basis: ReturnType<typeof armedBasis>): string {
    if (armed.kind === 'ghost' || basis === null) return 'null'
    return 'tile' in basis ? `tile:${basis.tile}` : `take:${basis.take}`
}

export function Wall({
    theme, initialWallPage, profile: initialProfile = '', facetOffer, anonPage, initialMine, signedIn, ssrContainerWidth, renderHeader, renderSignIn,
    renderConsentCard, footer, arrivalNotice = null, renderIdentityIntake,
}: WallProps) {
    /** The house's visitor policy, in force only while the reader is signed out — Ovid's signed-in
     *  wall is Toile's wall, and Toile/Narcis have no policy to be in force at all. One value,
     *  read wherever a visitor's wall differs. */
    const visitor = signedIn ? undefined : theme.visitor
    const [searchParams] = useSearchParams()
    const location = useLocation()
    /** The history entry this wall is being read on, taken once: the `?q=` mirror below rewrites
     *  that same entry with `replaceState`, so its key stands for the whole visit, and a Back
     *  returns to it carrying that key — which is what `wallReturn.ts` matches a snapshot on. */
    const locationKeyRef = useRef(location.key)

    // `q` is local state, not the URL param read directly: a controlled input whose value comes
    // straight from `searchParams` drops keystrokes under fast typing — each keystroke fires a
    // router navigation, and if a second keystroke lands before that navigation commits, the input
    // re-renders from the still-stale URL-derived value and the keystroke is lost (observed in dev
    // verification, 2026-08-31: typing "koi" quickly left `?q=i`, the last character only). Local
    // state is the immediate source of truth for typing and for `useWallPages`; the URL is a
    // debounced MIRROR of it, for a shareable/bookmarkable link — never the other way around.
    // The initial value comes from `useSearchParams` rather than `window.location` so the server
    // render and the hydrating client render read the same URL — and through the same reading the
    // route loader used to SSR the first page (`readWallQueryParam`, N2), so the field, the painted
    // page and `useWallPages`' first-fetch guard all name one query.
    const [q, setQ] = useState(() => readWallQueryParam(searchParams))
    // Counts the field's explicit answers (Enter, a pill or a suggestion taken): a new count answers the wall at once.
    const [settleEpoch, setSettleEpoch] = useState(0)
    const settleNow = useCallback(() => setSettleEpoch((n) => n + 1), [])
    // The profile key (`profile.ts`): named by the document, moved by this page's own touches (`touch`), carried on
    // the wall events and on every wall read.
    const [profileKey, setProfileKey] = useState(initialProfile)
    const profileRef = useRef(profileKey)
    profileRef.current = profileKey
    // Counts the answers to a steering touch (a lean word dropped or taken back): the wall is read again from its top
    // with the new key, as it is when a pill changes. A plain touch moves the key and leaves the tiles where they are.
    const [steerEpoch, setSteerEpoch] = useState(0)
    // A write to the visitor's touches (`POST /touch`): the server answers the profile key they make now.
    // The route commits the session whole, so the sends go one at a time and only the last answer is applied
    // (`touchQueue.ts`).
    const sendTouchRef = useRef<((body: TouchBody) => void) | null>(null)
    if (sendTouchRef.current === null) {
        sendTouchRef.current = createTouchQueue(
            async (body) => {
                const res = await fetch('/touch', {
                    method: 'POST',
                    headers: {'Content-Type': 'application/json'},
                    body: JSON.stringify(body),
                })
                return res.ok ? readTouchAnswer(await res.json()) : null
            },
            (answer, steered) => {
                setProfileKey(answer.profile)
                if (steered) setSteerEpoch((n) => n + 1)
            },
        )
    }
    const sendTouch = useCallback((body: TouchBody) => sendTouchRef.current!(body), [])
    // A touch: the tile joins the visitor's touches.
    const touch = useCallback((tileId: number | null | undefined) => {
        if (tileId) sendTouch({tile: tileId})
    }, [sendTouch])
    // The pills (`"indigo dye"` inside `q`) and the words typed beside them: one query, read through
    // `parseQuery` wherever the two must be told apart (the bar, the chips' pressed state, the ghost).
    const parsedQ = useMemo(() => parseQuery(q, (token) => isHouseFacet(theme.id, token)), [q, theme.id])
    const {pills, words: typedWords} = parsedQ
    const qRef = useRef(q)
    qRef.current = q
    // Where the search being typed or settled began (`wall_search.from`): set by a pick, spent by the event.
    const searchFromRef = useRef<WallSearchFrom>('typed')
    // The pills as the field wears them (phrases, then facet values), and what the ghost and the Make row carry.
    const yours = useMemo(() => yoursOf(parsedQ, theme.facets), [parsedQ, theme.facets])
    const ghostDirection = useMemo(() => makeDirection(yours, typedWords), [yours, typedWords])
    // The last settled searches of this browser; read after mount, the server has no storage.
    const [recent, setRecent] = useState<string[]>([])
    useEffect(() => setRecent(readRecent(browserStore(), theme.id)), [theme.id])
    const commitRecent = useCallback((settled: string) => {
        setRecent((prev) => {
            const next = pushRecent(prev, settled)
            writeRecent(browserStore(), theme.id, next)
            return next
        })
    }, [theme.id])
    // The lean word just dropped (a notice with Undo).
    const [leanNotice, setLeanNotice] = useState<{token: FieldToken; line: string} | null>(null)
    useEffect(() => {
        if (!leanNotice) return
        const timer = setTimeout(() => setLeanNotice(null), NOTICE_MS)
        return () => clearTimeout(timer)
    }, [leanNotice])
    // Each phrase a tile's chip lands as a pill rings the field once.
    const [landed, setLanded] = useState(0)
    // A chip tap puts its phrase in the bar as a pill, or lifts it (`from` 'chip_caption' | 'chip_hover');
    // the pill's own × in the bar reports as 'pill'. Every one is a touch: the event carries the phrase, so
    // a profile can be built from it later; `active` says whether the tap put the phrase in or took it out.
    const onChipTap = useCallback((phrase: string, from: WallChipTappedFrom, tileId: number | null) => {
        const held = pills.some((p) => samePhrase(p, phrase))
        const ref = {type: 'phrase', value: phrase} as const
        const next = held ? withoutToken(q, ref) : withToken(q, ref)
        if (!held && next.length > MAX_DIRECTION_CHARS) return
        track('wall_chip_tapped', {house: theme.id, from, token: phrase, active: !held, tile_id: tileId ?? undefined, profile: profileRef.current || undefined})
        touch(tileId)
        if (!held) {
            searchFromRef.current = 'phrase'
            setLanded((n) => n + 1)
            commitRecent(next)
        }
        setQ(next)
        settleNow()
    }, [q, pills, theme.id, touch, commitRecent, settleNow])
    const onPillRemove = useCallback(
        (phrase: string) => track('wall_chip_tapped', {house: theme.id, from: 'pill', token: phrase, active: false, profile: profileRef.current || undefined}),
        [theme.id],
    )
    // The pills changing returns the wall to its top, where the header always shows: the reader sees the
    // pill land and the wall answer it. Not on the first render, and not for typed words.
    const pillsKey = pills.join('\n')
    const pillsSeenRef = useRef(pillsKey)
    useEffect(() => {
        if (pillsSeenRef.current === pillsKey) return
        pillsSeenRef.current = pillsKey
        window.scrollTo({top: 0})
    }, [pillsKey])

    useEffect(() => {
        const timer = setTimeout(() => {
            const params = new URLSearchParams(window.location.search)
            if ((params.get('q') ?? '') === q) return
            if (q) params.set('q', q)
            else params.delete('q')
            replaceUrl(params)
        }, 300)
        return () => clearTimeout(timer)
    }, [q])

    // Code-quality review F9: named `isMobile` until this fix, which made the file argue with
    // itself — `isSheetSurface()` (the helper that fills it) is a statement about the POINTER, not
    // the device, so a 900px touch laptop reported `consent_shown{surface: 'sheet'}` while sitting
    // on a desk. The helper was already named correctly; the state it fills now is too.
    const [sheetSurface, setSheetSurface] = useState(false)
    // The ghost follows every keystroke; the wall's order waits for the typing to pause, or answers at once on a space,
    // Enter, a pill or a suggestion (`answerPause.ts`).
    const settledQ = useSettledQuery(q, {sheet: sheetSurface, settleEpoch})
    // A closed house's visitor has no catalog to page (`useWallPages` stays on its empty seed and never
    // fetches); the search field instead asks the house's reader for the veiled matches (`useAnonPage`).
    const wallPages = useWallPages(anonPage ? '' : settledQ, initialWallPage ?? NO_WALL_PAGE, profileKey, anonPage ? 0 : steerEpoch)
    const visitorAnswer = useAnonPage(settledQ, anonPage)
    const visitorPage = visitorAnswer?.page
    // `wallPages.loadedQuery` — the already-debounced, already-settled query `/wall` just answered
    // — drives `/mine?q=` too (search plan § Decided "follows the wall search's own cadence"): no
    // second debounce timer, and the two reads answer the same query at the same beat.
    const {mine, answeredQuery, refetchMine, mergeTake, mergePinned, mergeByTile, setIdentity, setAllowance, sessionExpired} =
        useMine(initialMine, signedIn, wallPages.loadedQuery)
    // A visitor's free take (`HouseTheme.freePick`): `mine` holds it while signed out. `owns` drives everything
    // that reads or stamps the reader's own rows; `freePickIsOpen` says the first Generate may still write.
    const owns = ownsRows(signedIn, mine)
    const freePickIsOpen = freePickOpen(theme, signedIn, mine)
    // The identity slot's own state (plan `2026_09_17_ovid_wall_verbs.md` § Decided 5/6) — `{kind:
    // 'none'}` is Toile's own value on the wire (`contract.ts`'s `Identity` union) and doubles as
    // the fallback before `/mine` has ever answered (anon, or the very first paint): armable either
    // way (`chains.identityArmable`), so no house is disarmed by a response it hasn't seen yet.
    // A visitor at a house with an intake is a person with no model yet: the same word and the same
    // door as a member's missing model (`chains.wallIdentity`); sign-in waits for the intake's Start.
    const identity: Identity = wallIdentity(mine?.identity, signedIn, Boolean(renderIdentityIntake))
    // Bumped once per `/continue` refusal for a missing identity (409 `identity_required`) — see
    // `Column.tsx`'s own comment for how this counter becomes `IdentityPanelProps.open`; `fire`
    // below is the one writer.
    const [identityOpenSignal, setIdentityOpenSignal] = useState(0)

    const [armed, setArmed] = useState<Armed | null>(null)
    const [overlayOpen, setOverlayOpen] = useState(false)
    const [direction, setDirection] = useState('')
    /** The ratio the user PICKED, or null while the frame simply states the shape on screen. The
     *  ratio is askable at any point of a chain (operator decision 2026-08-31), so this is an
     *  override of the chain's own inheritance, not a value the wall always holds. */
    const [ratioPick, setRatioPick] = useState<RatioKey | null>(null)
    /** The step the counter points at — a take, or the chain's basis tile (`chains.chainSteps`);
     *  `null` = the chain's newest (BRIEF § Regenerate). */
    const [selectedStepId, setSelectedStepId] = useState<string | null>(null)
    /** Every take this page session has seen, per `chains.holdTakes` — the counter's own source, since
     *  `/mine` only ever returns a chain's newest done row. */
    const [takeLedger, setTakeLedger] = useState<TakeLedger>(() => new Map())
    const [signInOpen, setSignInOpen] = useState(false)
    const [gateRedirect, setGateRedirect] = useState('/')
    /** What raised the card: `wall` (a Generate, or the pin), `share` (the Share door). Read by `renderSignIn`. */
    const [gateTrigger, setGateTrigger] = useState<string>(GATE_TRIGGER_WALL)
    /** The one way the card opens: where it returns to, and what asked. */
    const openSignIn = useCallback((redirect: string, trigger: string = GATE_TRIGGER_WALL) => {
        setGateRedirect(redirect)
        setGateTrigger(trigger)
        setSignInOpen(true)
    }, [])
    /** The card opened for a take: the return token re-arms its chain after the round trip. */
    const signInFor = useCallback((take: Take, trigger: string) => {
        const target: Armed = take.root_tile_id !== null
            ? {kind: 'tile', tileId: take.root_tile_id}
            : {kind: 'scratch', rootId: take.root_id}
        openSignIn(returnTokenFor(target, '', null), trigger)
    }, [openSignIn])
    /** `armed` is left untouched while the intake is open, so closing returns to the same tile. */
    const [intakeOpen, setIntakeOpen] = useState(false)
    /** A visitor's quick look (plan `2026_09_18_ovid_wall_polish.md` § P5) — an OPEN anon tile's own
     *  picture, held only long enough to show the locked column/sheet; a veiled cell and the ghost's
     *  door still go straight to `openVisitorSignIn` (unchanged), never through this state. Distinct
     *  from `armed`/`overlayOpen`, which model a real generation chain a visitor has none of. */
    const [visitorLocked, setVisitorLocked] = useState<Extract<AnonTile, {image_key: string}> | null>(null)
    // The terms gate (plan 2026_09_01_tos_gate.md §8.4) — one instance for the whole wall. Column
    // and Sheet are two independent conditionals below, both gated on `sheetSurface` (F13: the
    // earlier comment here said "keyed on", implying one mounted element choosing between them — it
    // is two `overlayOpen && armedView && (!)sheetSurface` conditionals, so crossing the boundary on
    // a resize unmounts one and mounts the other rather than re-keying a single element). `surface`
    // follows the same value, so `consent_shown`/`consent_accepted` name whichever one is on screen.
    const gate = useConsentGate({house: theme.id, surface: sheetSurface ? 'sheet' : 'column'})
    const [containerWidth, setContainerWidth] = useState(ssrContainerWidth ?? SSR_CONTAINER_WIDTH)
    const [viewportW, setViewportW] = useState(SSR_CONTAINER_WIDTH)
    const [viewportH, setViewportH] = useState(SSR_VIEWPORT_H)
    /** False until the layout effect below has replaced the `SSR_*` assumptions with the real
     *  numbers. Nothing that depends on where a row actually sits may run before it — at phone
     *  width the assumed 1200px container is a different wall entirely. */
    const [measured, setMeasured] = useState(false)
    const [scrollTop, setScrollTop] = useState(0)
    const [pillDismissedAt, setPillDismissedAt] = useState<number | null>(null)
    /** Chains with a `/continue` in flight — the cell shimmers from the click, not from the poll. */
    const [firing, setFiring] = useState<ReadonlySet<ChainKey>>(() => new Set())
    /** The one-off line: a failure, or a `/continue` that did not land (B2). Said once, then gone. */
    const [notice, setNotice] = useState<{key: ChainKey; line: string} | null>(null)
    /** Back from the allowance's checkout, until the next press (`HouseTheme.allowanceReturn`). */
    const [allowanceWelcome, setAllowanceWelcome] = useState(false)
    /** Tile summaries fetched by id for a `?basis=` that is not on a loaded page (S5). */
    const [fetchedTiles, setFetchedTiles] = useState<ReadonlyMap<number, TileSummary>>(() => new Map())
    /** The batch named by `?batch=` on arrival from a page verb (plan e). Held for the page session
     *  rather than read from the URL: the param is stripped the moment it is read, and the nine must
     *  stay where the visitor landed until they leave — a stamp must not re-sort them. Another
     *  user's batch names no row of `/mine`, so it paints nothing and the param is stripped all the
     *  same. */
    const [arrivalBatchId, setArrivalBatchId] = useState<string | null>(null)

    const wallRef = useRef<HTMLDivElement>(null)
    const signInRef = useRef<HTMLDivElement>(null)
    const wallTopRef = useRef(0)
    /** The page load's clock — the first entry's `now` (`Hold`); every later entry reads its own.
     *  A ref so the server render and the hydrating client render each read it once. */
    const nowRef = useRef(Date.now())
    /** The last press `fire()` accepted, across every chain — an accidental double-tap of the same
     *  body within the window (`chains.isDuplicatePress`) is dropped before anything else happens;
     *  a deliberate repeat, or a press of a different body, is untouched (operator decision
     *  2026-09-01). Not React state: a duplicate must be caught on the very next press, synchronously,
     *  never a render late. */
    const lastAcceptedPress = useRef<Press | null>(null)
    /** The `request_id` sent with the last press of a given body, so a deliberate retry of the SAME
     *  words/basis/ratio (outside `isDuplicatePress`'s 400ms window, so it is not dropped client-side
     *  — the user pressing Generate again after a `failed` notice) reuses the same key instead of
     *  minting a fresh one (error-handling review F5). `continueBody`'s own `body` stays free of it —
     *  `pressKeyOf` must keep comparing content, not a per-attempt id — so this lives beside it
     *  rather than inside it. A single slot, not a map: only the immediately preceding press's body
     *  can be "the same idea, retried"; a genuinely different body starts a fresh id. */
    const requestIdForRetryRef = useRef<{bodyKey: string; requestId: string} | null>(null)
    /** Ids `/seen` has actually stamped — added on a 2xx only, so a 429 or a dropped connection
     *  leaves the row unseen and the next pass tries again (N5/N10). */
    const seenPostedRef = useRef<Set<string>>(new Set())
    /** Rows whose `wall_generation_failed` has been reported. Reported once per row, whatever the
     *  stamp does (N5) — the event is the observation, the stamp is the bookkeeping. */
    const failedNotedRef = useRef<Set<string>>(new Set())
    /** Rows whose `wall_generation_done` has been reported (decision 6, plan
     *  `2026_09_21_wall_gaps.md`) — the mirror of `failedNotedRef` above, once per row. */
    const doneNotedRef = useRef<Set<string>>(new Set())
    /** One `/seen` in flight at a time, whichever effect below started it (least-and-simplest
     *  review §2.8: the arrival-paint effect and the failed-row effect each guarded their own call
     *  to the SAME endpoint separately, which let one of each be in flight against `/seen`
     *  simultaneously). Whichever caller loses this guard skips a tick and retries on its own next
     *  poll — already the documented retry contract for a `null` result (`stampSeen`'s own doc). */
    const seenInFlight = useRef(false)
    /** Every id `GET /tile/:id` has been asked for. The `fetchedTiles` STATE lags the fetch that
     *  filled it by a render, and the return-token effect's `arm` closure reads the mount-time map
     *  — which is how one token produced two identical `GET /tile/:id` (N9). */
    const requestedTilesRef = useRef<Set<number>>(new Set())
    /** Chains whose history has been ASKED for this page session (`GET /chain/:root`) — one ask per
     *  chain, marked before the fetch so it is the in-flight guard too. A chain is asked once and
     *  never again: from that point the poll keeps the ledger current by itself, and a 404 (a chain
     *  gone, another user's) must not turn every re-arm into another request. */
    const chainHistoryAskedRef = useRef<Set<ChainKey>>(new Set())
    const searchFiredForRef = useRef<string | null>(null)
    /** The query a restored page set (`wallReturn.ts`, a Back) already answered — `wall_search`
     *  fired for it on the way OUT, so the restore must not report it again on the way back in. Set
     *  by the restore layout effect below, which — being a layout effect — always settles before
     *  the `wall_search` passive effect can read it for the same commit; it goes stale the moment
     *  the query actually changes, which is exactly when a fresh report is due. */
    const restoredQueryRef = useRef<string | null>(null)
    const tokenReadRef = useRef(false)
    /** Set by a return token carrying `render`: the chain it re-armed renders once, by the verb's own path. */
    const [renderOnReturn, setRenderOnReturn] = useState(false)

    /** Every tile the wall holds a summary of — the loaded pages, then the ones `GET /tile/:id`
     *  fetched — whose own chains `useOwnByTile` asks for beyond `/mine`'s window. */
    const knownTileIds = useMemo(
        () => [...wallPages.tiles.map((t) => t.id), ...fetchedTiles.keys()],
        [wallPages.tiles, fetchedTiles],
    )
    useOwnByTile(owns && !anonPage && mine !== null, knownTileIds, mergeByTile)

    const chains = useMemo(() => buildChains(mine, mine?.byTile), [mine])
    /** `chains` as of the current render, reachable from a callback that must not be rebuilt on
     *  every poll — the same during-render ref assignment `tileByIdRef` uses. */
    const chainsRef = useRef(chains)
    chainsRef.current = chains

    // The cell at work (`thinking/plans/2026_09_12_wall_preview_frames.md`, § Carrier "Files"):
    // the row's 1-based place in the user's own line. The line INCLUDES the row on the machine: a
    // `rendering` row is place 1 (the claim query admits at most one per user, `arm.server.ts`), so
    // the `queued` rows count on from there — sorted `createdAt` then `position` (the tiebreaker
    // inside one batch, whose rows share a `createdAt`). Numbering only the queued rows put two 1s
    // on screen the moment the head was claimed (operator on toile-dev, 2026-09-12). Recomputed
    // only when `mine` changes, same cadence as `chains` above.
    const queuedOrder = useMemo(() => {
        const order = new Map<string, number>()
        if (!mine) return order
        const ahead = mine.pending.filter((p) => p.status === 'rendering').length
        const queued = mine.pending
            .filter((p) => p.status === 'queued')
            .slice()
            .sort((a, b) => a.created_at.localeCompare(b.created_at) || (a.position ?? 0) - (b.position ?? 0))
        queued.forEach((take, i) => order.set(take.id, i + 1 + ahead))
        return order
    }, [mine])

    // The probe's own input: every row actually `rendering` right now (never `queued` — there is
    // nothing to fetch before the service has started). A fresh array identity on every `mine`
    // poll tick is what drives the probe's own "next poll tick" cadence (`usePreviewFrames.ts`'s
    // own header doc).
    const renderingRowIds = useMemo(
        () => (mine ? mine.pending.filter((p) => p.status === 'rendering').map((p) => p.id) : []),
        [mine],
    )
    const previewFrames = usePreviewFrames(renderingRowIds, theme.previewImageUrl)

    // The counter's record: every row `/mine` has shown this session, kept as rows come and go from the
    // wire (a chain's earlier done take leaves `done` the moment its successor renders).
    //
    // `wall_generation_done` (decision 6, plan `2026_09_21_wall_gaps.md`) reads `takeLedger`
    // (`chains.justTurnedDone`) BEFORE this tick's own `holdTakes` call folds `mine`'s rows into
    // it — the pure half of the check (`justTurnedDone`) lives in chains.ts precisely so it is
    // testable without a DOM; this effect only sequences it. `takeLedger` is deliberately NOT a
    // dependency: this effect must see last tick's ledger, not react to the very update it makes
    // on every run.
    useEffect(() => {
        for (const row of justTurnedDone(takeLedger, mine?.done ?? [])) {
            if (doneNotedRef.current.has(row.id)) continue
            doneNotedRef.current.add(row.id)
            track('wall_generation_done', {kind: takeGenerateKind(row), tile_id: row.root_tile_id ?? undefined})
        }
        setTakeLedger((prev) => holdTakes(prev, mine))
        // eslint-disable-next-line react-hooks/exhaustive-deps -- `takeLedger` read as last tick's value, deliberately
    }, [mine])

    // ── viewport + layout measurement ──────────────────────────────────────────────────────────
    // Everything measured here is read at `SSR_*` values during the server render AND the hydrating
    // client render (N3) — one HTML, no mismatch — and replaced with the real numbers in this
    // layout effect, which runs after hydration and before the first paint.
    useIsomorphicLayoutEffect(() => {
        function measure() {
            setSheetSurface(isSheetSurface())
            setViewportW(window.innerWidth)
            setViewportH(window.innerHeight)
            if (wallRef.current) {
                const width = contentWidth(wallRef.current)
                setContainerWidth(width)
                writeWallWidthCookie(width)
                wallTopRef.current = wallRef.current.getBoundingClientRect().top + window.scrollY
            }
        }
        measure()
        setMeasured(true)
        const observer = new ResizeObserver(measure)
        if (wallRef.current) observer.observe(wallRef.current)
        window.addEventListener('resize', measure)
        return () => {
            observer.disconnect()
            window.removeEventListener('resize', measure)
        }
    }, [])

    useEffect(() => {
        let raf = 0
        function onScroll() {
            if (raf) return
            raf = requestAnimationFrame(() => {
                setScrollTop(window.scrollY)
                raf = 0
            })
        }
        window.addEventListener('scroll', onScroll, {passive: true})
        return () => {
            window.removeEventListener('scroll', onScroll)
            if (raf) cancelAnimationFrame(raf)
        }
    }, [])

    // ── the return to the wall — the page set and the offset, put back before the paint ────────
    /**
     * A pack label is a `<Link>`, so leaving for a pack page and coming Back is a POP inside the
     * same document and this component simply re-mounts (verified on toile dev, 2026-09-01). Only
     * the FIRST page comes from the route loader; every page after it lives in `useWallPages`. So
     * the wall came back one page tall, and every scroll restoration there is — react-router's
     * `<ScrollRestoration>` on a POP (`root.tsx`), the browser's own on a fresh load — clamped to
     * the bottom of that one page, which reads as "the wall re-rendered at the top".
     *
     * `wallReturn.ts` holds the page set and the offset for the tab; this puts both back. It runs
     * from a layout effect and never from the first render's state: a Back that arrives as a fresh
     * document load HYDRATES the server's page-one HTML, and a first render carrying different
     * tiles would throw that tree away (the N3 mistake). Three commits, all of them before the
     * browser paints — the measurement, then the restored pages (with `scrollTop` moved along with
     * them, so the windowing paints the rows the reader is about to see instead of a screen of
     * spacers), then the scroll itself, once those rows are in the DOM at their true width.
     */
    const restoreAskedRef = useRef(false)
    const pendingScrollRef = useRef<number | null>(null)
    useIsomorphicLayoutEffect(() => {
        if (!measured) return
        if (pendingScrollRef.current !== null) {
            const y = pendingScrollRef.current
            pendingScrollRef.current = null
            window.scrollTo(0, y)
            return
        }
        if (restoreAskedRef.current) return
        restoreAskedRef.current = true
        const snapshot = readWallReturn(sessionStore(), {
            house: theme.id, locationKey: locationKeyRef.current, q,
        })
        if (!snapshot) return
        pendingScrollRef.current = snapshot.scrollY
        setScrollTop(snapshot.scrollY)
        // The restored pages already answer `snapshot.q` — `wall_search` reported it on the way
        // out, so this render must not report it a second time on the way back in (item 1, wall
        // plan d carried items).
        restoredQueryRef.current = snapshot.q
        wallPages.restorePages({
            query: snapshot.q,
            tiles: snapshot.tiles,
            nextCursor: snapshot.nextCursor,
            seamAt: snapshot.seamAt,
            matchedCount: snapshot.matchedCount,
        })
        // The identity read here is the MOUNT's `q` and history key, held by `restoreAskedRef`;
        // `wallPages.tiles` is in the deps only to carry the effect into the commit that holds the
        // restored rows, which is where the scroll can finally land.
    }, [measured, wallPages.tiles])

    /** What a return is written from, reachable from the writer below, which mounts once and must
     *  still read the page set as it stands — the same during-render ref assignment `chainsRef`
     *  uses. */
    const returnState = {
        tiles: wallPages.tiles, nextCursor: wallPages.nextCursor,
        seamAt: wallPages.seamAt, matchedCount: wallPages.matchedCount, q: wallPages.loadedQuery,
    }
    const returnStateRef = useRef(returnState)
    returnStateRef.current = returnState

    // Two ways out of the wall, one writer: a client navigation, which unmounts this component,
    // and `pagehide`, which is the document being frozen for bfcache or torn down for a real load.
    // A LAYOUT effect: its unmount cleanup runs in the mutation phase of the navigation's commit,
    // before `<ScrollRestoration>`'s layout effect scrolls the new page to the top, so
    // `window.scrollY` is still the wall's. As a passive effect the cleanup ran after that reset
    // and stored 0 — masked until 2026-09-23 by a smooth-scroll rule that made the reset animate.
    useIsomorphicLayoutEffect(() => {
        function persist() {
            const held = returnStateRef.current
            writeWallReturn(sessionStore(), {
                house: theme.id,
                locationKey: locationKeyRef.current,
                q: held.q,
                tiles: held.tiles,
                nextCursor: held.nextCursor,
                seamAt: held.seamAt,
                matchedCount: held.matchedCount,
                scrollY: window.scrollY,
            })
        }
        window.addEventListener('pagehide', persist)
        return () => {
            window.removeEventListener('pagehide', persist)
            persist()
        }
    }, [theme.id])

    /** `gate.fold` as of the current render, reachable from `returnToWall` without making that
     *  callback's own identity depend on `gate` — the same during-render ref assignment `chainsRef`
     *  uses. `useConsentGate` returns a fresh object (and fresh methods) every render (its own header
     *  doc), so closing over `gate.fold` directly would remake `returnToWall` — and the scroll-return
     *  listener that depends on it — on every render. */
    const gateFoldRef = useRef(gate.fold)
    gateFoldRef.current = gate.fold

    /**
     * The return — the column's own strip says how it is asked for: "↓ Wall · esc · scroll · or
     * click the wall" (BRIEF §6, board ToileArmed). Every one of those routes here (N13), so they
     * all leave exactly the same state behind: the armed chain is kept, and the pill carries it
     * back — showing itself whenever there is work to show, staying away when there is none.
     * Escape now arrives through the layer's own focus trap (`useFocusTrap`), which is also what
     * puts focus back on the tile that opened it.
     *
     * Folds the consent gate too (code-quality review F2): a first-ever Generate opens the consent
     * card inside the column, and only Escape's own layered handler (`Column.tsx`'s/`Sheet.tsx`'s
     * `onEscape`, which folds the gate before it ever reaches `onClose`) used to fold it — the scrim
     * click, the scroll/touch return and this same "↓ Wall" strip all routed here instead, leaving
     * `gate.open` true with nothing on screen. `data-wall-overlay` stayed `"open"` on that state
     * (still `gate.open`, per the effect above), freezing the header shown for the rest of the
     * page's life — the same symptom the header pin leak fixed in `51b459128`, reached through the
     * third boolean. Folding an already-folded/never-opened gate is a no-op (`gate.ts`'s own `FOLD`
     * handler).
     *
     * The visitor's own locked quick-look (plan `2026_09_18_ovid_wall_polish.md` § P5) shares this
     * same return — "Escape, the scrim and the scroll-return close it exactly as for a member"
     * (plan's own words) — so it folds alongside `overlayOpen`; `gate.fold` is a no-op for a
     * visitor, who never opens the consent gate at all.
     */
    const returnToWall = useCallback(() => {
        setOverlayOpen(false)
        setVisitorLocked(null)
        gateFoldRef.current()
    }, [])

    /**
     * A word door tapped (board `WordsAsDoors`, validated 2026-09-18): the word becomes the wall's
     * query, exactly as if typed — the field shows it with its ×, and the existing `?q=` read path
     * re-orders the wall. A tap from the armed caption folds the column/sheet first, the same
     * return every other gesture takes (`chains.wordTapFolds`); a tile's own hover chip has nothing
     * open to fold — "sets the query without arming" (board row 2 frame 3). One event either way,
     * distinguished by `from` so the two surfaces are countable apart.
     */
    // `tileId` (decision 2, plan `2026_09_21_wall_gaps.md`) — the caption's own armed tile for a
    // `'caption'` tap, or the tile under the cursor for a `'hover'` tap; each call site below knows
    // its own tile, so this reads it as a plain argument rather than re-deriving it here.
    // A chip (a door with a phrase) goes into the bar as a pill; the take's typed direction (no phrase)
    // replaces the typed words and searches, the pills staying.
    const onWordTap = useCallback((door: Door, from: WallWordTappedFrom, tileId: number | null) => {
        if (door.phrase !== null) {
            onChipTap(door.phrase, from === 'caption' ? 'chip_caption' : 'chip_hover', tileId)
        } else {
            track('wall_word_tapped', {house: theme.id, from, word_len: door.label.length, tile_id: tileId ?? undefined})
            setQ(composeQuery(pills, door.label, parsedQ.facets))
        }
        if (wordTapFolds(from)) returnToWall()
    }, [theme.id, returnToWall, onChipTap, pills, parsedQ.facets])
    const onCaptionWordTap = useCallback(
        (door: Door) => onWordTap(door, 'caption', armed ? armedTileId(armed, chains) : null),
        [onWordTap, armed, chains],
    )
    const onHoverWordTap = useCallback(
        (door: Door, tileId: number | null) => onWordTap(door, 'hover', tileId),
        [onWordTap],
    )

    // Scroll is the return (BRIEF §6): once the column/sheet is open, a wheel or touch drag of more
    // than a few pixels folds it back into the pill — momentum keeps scrolling. This listens on the
    // GESTURE (`wheel`/`touchmove`), not on the resultant `window.scrollY` the old effect watched.
    // Incident, 2026-09-01: iOS Safari scrolls the layout viewport to lift a focused input clear of
    // the rising keyboard (the Sheet is `position: fixed` at the bottom), and a tablet's focus trap
    // focusing the field on open summons the same keyboard inside the tap's own activation window —
    // both produce a `window.scrollY` delta with no wheel or touch behind it, and the old effect read
    // that browser-driven scroll as the user's own return gesture, closing the layer it had just
    // opened and dismissing the keyboard with it. A gesture that STARTS inside the layer's own
    // interactive content (the field, the ratio row, the consent card, the counter — each marked
    // `data-wall-return-exempt`) is not a return either: a drag to select text or to pick a ratio
    // pill must reach its own handler untouched. The Sheet's grabber keeps its own swipe-down handler
    // regardless (`Sheet.tsx`).
    useEffect(() => {
        if (!overlayOpen && !visitorLocked) return

        function isExempt(target: EventTarget | null): boolean {
            return target instanceof Element && target.closest('[data-wall-return-exempt]') !== null
        }

        let wheelAccum = 0
        function onWheel(e: WheelEvent) {
            if (isExempt(e.target)) return
            wheelAccum += Math.abs(e.deltaY)
            if (wheelAccum > SCROLL_RETURN_THRESHOLD) returnToWall()
        }

        let touchStartY: number | null = null
        let touchExempt = false
        /** The touch began on the phone sheet itself (`Sheet.tsx`, `data-wall-sheet`): there only a
         *  DOWNWARD drag is the return — the swipe down of BRIEF §14 — so a finger drifting up while
         *  reaching for the bar folds nothing. */
        let touchOnSheet = false
        function onTouchStart(e: TouchEvent) {
            touchExempt = isExempt(e.target)
            touchOnSheet = e.target instanceof Element && e.target.closest('[data-wall-sheet]') !== null
            touchStartY = e.touches[0]?.clientY ?? null
        }
        function onTouchMove(e: TouchEvent) {
            if (touchExempt || touchStartY === null) return
            const y = e.touches[0]?.clientY
            if (y === undefined) return
            const dy = y - touchStartY
            if ((touchOnSheet ? dy : Math.abs(dy)) > SCROLL_RETURN_THRESHOLD) returnToWall()
        }

        document.addEventListener('wheel', onWheel, {passive: true})
        document.addEventListener('touchstart', onTouchStart, {passive: true})
        document.addEventListener('touchmove', onTouchMove, {passive: true})
        return () => {
            document.removeEventListener('wheel', onWheel)
            document.removeEventListener('touchstart', onTouchStart)
            document.removeEventListener('touchmove', onTouchMove)
        }
    }, [overlayOpen, visitorLocked, returnToWall])

    // The header's overlay freeze (plan 2026_09_04_wall_header_reveal.md § Step 1) — the header
    // cannot see the column/sheet, the sign-in card or the consent card's own state, so this is what
    // tells it: one attribute on `<html>`, set wherever any of the three is open. `useHeaderReveal`
    // reads it via a `MutationObserver` rather than importing anything from `@core/wall` (the header
    // is a product component; this hook must not depend on it either way).
    useEffect(() => {
        const anyOverlayOpen = overlayOpen || !!visitorLocked || signInOpen || gate.open
        if (anyOverlayOpen) document.documentElement.setAttribute('data-wall-overlay', 'open')
        else document.documentElement.removeAttribute('data-wall-overlay')
        return () => document.documentElement.removeAttribute('data-wall-overlay')
    }, [overlayOpen, visitorLocked, signInOpen, gate.open])

    // `data-wall-surface` — the same channel, for the same reason (code-quality review F4 /
    // least-and-simplest review §2.3): `isSheetSurface()`'s own decision moved to `pointer: coarse`
    // under `51b459128`, and every phone-shaped CSS rule (wall padding, header search, the ratio
    // row's tap floor) was still keyed on the 640px width breakpoint alone — a touch tablet between
    // 641 and 1024px landed on the Sheet with the desktop's own shapes everywhere else. Published on
    // `<html>` wherever `sheetSurface` is measured, so the CSS decides off the one value the JS already
    // computed rather than re-deriving its own guess.
    useEffect(() => {
        document.documentElement.setAttribute('data-wall-surface', sheetSurface ? 'sheet' : 'column')
        return () => document.documentElement.removeAttribute('data-wall-surface')
    }, [sheetSurface])

    // Error-handling review F6: `useMine` stops polling on a second consecutive 401 and reports it
    // here rather than continuing to hit `/mine` every ten seconds with nothing on screen ever
    // saying the session ended. Opens the sign-in card the same way `fire`'s own 401 branch does —
    // a bare `/` redirect, since there is no armed press this expiry is answering.
    useEffect(() => {
        if (!sessionExpired) return
        openSignIn('/')
    }, [sessionExpired, openSignIn])

    // ── tiles by id: the loaded pages, then the chain's own joined root, then `GET /tile/:id` ──
    const loadedById = useMemo(() => {
        const map = new Map<number, TileSummary>()
        for (const t of wallPages.tiles) map.set(t.id, t)
        return map
    }, [wallPages.tiles])

    const tileById = useCallback(
        (id: number): TileSummary | null =>
            loadedById.get(id) ?? chains.get(tileChainKey(id))?.root ?? fetchedTiles.get(id) ?? null,
        [loadedById, chains, fetchedTiles],
    )

    /** `tileById` as of the current render, reachable from a callback that closed over an older
     *  one (N9) — the same during-render ref assignment `arrivalIdsRef` below uses. */
    const tileByIdRef = useRef(tileById)
    tileByIdRef.current = tileById

    const fetchTile = useCallback(async (id: number): Promise<TileSummary | null> => {
        requestedTilesRef.current.add(id)
        try {
            const res = await fetch(`/tile/${id}`)
            if (!res.ok) {
                // Error-handling review F2: only a 404 (an unknown or since-unlisted tile,
                // `tile.$id.ts`'s own rule) is meant to be permanent — a transient failure un-marks
                // the id so the next `ensureTile` pass retries instead of showing the fallback label
                // for the rest of the page session.
                if (res.status !== 404) requestedTilesRef.current.delete(id)
                return null
            }
            const tile: TileSummary = await res.json()
            setFetchedTiles((prev) => new Map(prev).set(tile.id, tile))
            return tile
        } catch {
            requestedTilesRef.current.delete(id)
            return null
        }
    }, [])

    const ensureTile = useCallback(
        (id: number) => {
            if (requestedTilesRef.current.has(id)) return
            if (!tileByIdRef.current(id)) void fetchTile(id)
        },
        [fetchTile],
    )

    /**
     * The armed chain's own history, asked once (`GET /chain/:root`, `core/wall/chain.server.ts`).
     * The counter reads the page session's ledger, and `/mine` returns a chain's newest done row only —
     * so a reload leaves a five-take chain with one take unless the history is fetched here. It is
     * fetched at ARM time rather than carried by the poll, whose cost must not grow with a chain's
     * depth.
     *
     * Nothing is asked for a chain `/mine` has not named: the user owns no row of it, so there is
     * no history to find (and this is the common case — every fresh tile the visitor arms). That
     * chain is not marked either: once a take of it exists, the next arm asks.
     */
    const seedChainHistory = useCallback(async (target: Armed, knownToExist = false) => {
        if (!owns) return
        const root = armedChainRoot(target)
        if (!root) return
        const key = armedChainKey(target)
        if (chainHistoryAskedRef.current.has(key)) return
        // `knownToExist` bypasses the "nothing to fetch" guard below (edge case 4, plan
        // `2026_09_04_wall_pin.md`: a pinned take's chain may sit beyond `/mine`'s window on a tile
        // no loaded page holds, so `chains` never named it; the caller already knows the chain
        // exists because a pin can only ever be a row the user made).
        if (!knownToExist && !chainsRef.current.has(key)) return
        chainHistoryAskedRef.current.add(key)
        try {
            const res = await fetch(`/chain/${chainRootPath(root)}`, {credentials: 'same-origin'})
            if (!res.ok) {
                // Error-handling review F2: only the 404 case above is meant to be permanent — a
                // network drop or a 500 un-marks the key so the next arm of this chain retries,
                // rather than a transient failure turning into "the counter shows 1/1 for a
                // five-take chain for the rest of the page session."
                if (res.status !== 404) chainHistoryAskedRef.current.delete(key)
                return
            }
            const data = (await res.json()) as ChainResponse
            // Error-handling review F3: an unvalidated cast fed straight into `recordTakes`, whose
            // callers read `take.id`/`.status`/`.seen_at`/`.pinned_at` — a malformed 200 (a proxy
            // rewrite, a partial deploy) threw inside a React state updater, outside this `try`,
            // and took the wall down rather than showing nothing new. Treated the same as any other
            // failed fetch: un-mark and move on, the ledger keeps what the polls have given it.
            if (!Array.isArray(data.takes) || !data.takes.every(isTake)) {
                chainHistoryAskedRef.current.delete(key)
                return
            }
            setTakeLedger((prev) => recordTakes(prev, data.takes))
        } catch {
            // Transient failure — un-mark so the next arm retries; the ledger keeps what the polls
            // have given it in the meantime (one take rather than none).
            chainHistoryAskedRef.current.delete(key)
        }
    }, [owns])

    // ── arming ──────────────────────────────────────────────────────────────────────────────────
    const arm = useCallback(
        (next: Armed, from: WallArmedFrom, draft = '', opts: {stepId?: string; via?: WallMakeVia} = {}) => {
            setArmed(next)
            setDirection(draft.slice(0, MAX_DIRECTION_CHARS))
            setOverlayOpen(true)
            setNotice(null)
            // A chain opens on its newest take, at the shape that take already has — UNLESS a
            // specific step was named: a tap on a pinned cell arms the chain at that step (BRIEF
            // Iteration II §4), the take's own id being the step id `chains.takeStep` already uses.
            setSelectedStepId(opts.stepId ?? null)
            setRatioPick(null)
            if (next.kind === 'tile') ensureTile(next.tileId)
            // The takes of the chain being opened, whatever this page session happens to have seen.
            void seedChainHistory(next, !!opts.stepId)
            track('wall_tile_armed', {
                house: theme.id,
                from,
                basis: basisLabel(next, armedBasis(next, chains)),
                tile_id: armedTileId(next, chains) ?? undefined,
                chain: armedChainKey(next),
                via: opts.via,
                profile: profileRef.current || undefined,
            })
            touch(armedTileId(next, chains))
        },
        [chains, ensureTile, seedChainHistory, theme.id, touch],
    )

    const setFiringFlag = useCallback((key: ChainKey, on: boolean) => {
        setFiring((prev) => {
            const next = new Set(prev)
            if (on) next.add(key)
            else next.delete(key)
            return next
        })
    }, [])

    // ── firing a continue — one verb, one body builder (`chains.continueBody`) ─────────────────
    // The terms gate sits between the sign-in check and the write (plan §8.4: "auth first, then
    // consent"): a signed-out visitor never reaches the gate at all, and the gate's own `press`
    // wraps everything from the duplicate-press guard through the fetch, so a consent-deferred
    // press never reaches `/continue` and is never counted as the press that landed.
    const fire = useCallback(
        (target: Armed, rawDirection: string, pick: RatioKey | null = null, from: ChainStep | null = null) => {
            const key = armedChainKey(target)
            const token = returnTokenFor(target, rawDirection, pick, true)

            // §10: nothing is written anonymously, except a house's free first pick - the basis and the
            // words survive the card either way.
            if (!signedIn && !freePickIsOpen) {
                openSignIn(token)
                return
            }

            // Museless-disarmed (Ovid, plan `2026_09_17_ovid_wall_verbs.md` § Decided 5/6): the
            // verb is already `disabled` in `Column`/`Sheet` for this same reason, so this branch
            // is normally unreachable from the button itself — it stands as a belt-and-suspenders
            // check for any caller of `fire` that does not itself gate on a disabled state.
            // Bumping the signal here, ahead of the fetch, opens the strip on the same press that
            // would otherwise have been refused by the server. Its `gate_hit` was counted when the
            // column opened (`gateMet`), not here.
            if (!identityArmable(mine?.identity)) {
                setIdentityOpenSignal((n) => n + 1)
                return
            }

            setAllowanceWelcome(false)
            gate.press(async () => {
                const {body, kind} = continueBody({
                    armed: target,
                    chains,
                    direction: rawDirection,
                    ratioPick: pick,
                    houseRatio: theme.defaultRatio,
                    from,
                    identity,
                })

                // The accidental double-tap (operator decision 2026-09-01): the SAME body, fired
                // again within the window, is dropped here — before the event, the shimmer, and the
                // fetch — so it leaves no trace. A different body, or the same body after the
                // window, is a press of its own and reaches `/continue` like any other.
                const press: Press = {bodyKey: pressKeyOf(body), at: Date.now()}
                if (isDuplicatePress(lastAcceptedPress.current, press)) return
                lastAcceptedPress.current = press

                // Error-handling review F5: `/continue` can write the row and then 500 on shaping
                // its own response (a pool hiccup reading the root tile); the client saw `failed`
                // and had no idempotency key to retry safely with, so a genuine retry (outside
                // `isDuplicatePress`'s window) produced a second row for one intent. Reused across
                // an unchanged body, fresh for a changed one.
                const heldRequestId = requestIdForRetryRef.current
                const requestId = heldRequestId && heldRequestId.bodyKey === press.bodyKey
                    ? heldRequestId.requestId
                    : mintRequestId()
                requestIdForRetryRef.current = {bodyKey: press.bodyKey, requestId}

                setNotice(null)
                // Read at the press (the queue as it stood), tracked once the write has landed: a 401,
                // a refusal or a failed fetch is not a generation.
                const generated = {
                    kind,
                    queue_depth: mine?.pending.length ?? 0,
                    // What the row will be rendered at: the pick, or the shape it inherits.
                    ratio: effectiveRatio(
                        pick,
                        armedAspect({
                            armed: target,
                            chains,
                            basis: target.kind === 'tile' ? tileByIdRef.current(target.tileId) : null,
                            displayed: from,
                            ratioPick: pick,
                            houseRatio: theme.defaultRatio,
                        }),
                        theme.ratioKeys,
                    ),
                    tile_id: armedTileId(target, chains) ?? undefined,
                    chain: armedChainKey(target),
                }
                setFiringFlag(key, true)
                try {
                    const res = await fetch('/continue', {
                        method: 'POST',
                        headers: {'Content-Type': 'application/json'},
                        // `request_id` rides the wire body only — `pressKeyOf(body)` above is
                        // computed from `body` alone, so the duplicate-press guard keeps comparing
                        // content, never a per-attempt id.
                        body: JSON.stringify({...body, request_id: requestId}),
                    })
                    if (res.status === 401) {
                        openSignIn(token)
                        return
                    }
                    if (!res.ok) {
                        let error: ErrorResponse | null = null
                        try {
                            error = (await res.json()) as ErrorResponse
                        } catch {
                            // A body-less 4xx/5xx: the status alone names the line.
                        }
                        // Plan `2026_09_17_ovid_wall_verbs.md` § Decided 6: a race the proactive
                        // `identityArmable` guard above did not catch (`/mine` stale against
                        // another tab's choice, say) opens the identity panel instead of a generic
                        // pill line — the reader's own next action IS the fix, so naming it as an
                        // error would be the wrong register. House-agnostic: a house with no
                        // `identityPanel` never sends this code in the first place.
                        if (error?.error === 'identity_required') {
                            track('gate_hit', {gate: 'muse', trigger: GATE_TRIGGER_WALL})
                            setIdentityOpenSignal((n) => n + 1)
                            return
                        }
                        // A spent allowance is the bar's to say, in its own row (`HouseTheme.allowanceBar`):
                        // it joins `/mine`'s state, and the pill's word is not raised beside it.
                        const allowance = allowanceRefusal(res.status, error)
                        if (allowance && theme.allowanceBar) {
                            setAllowance(allowance)
                            return
                        }
                        setNotice({key, line: errorLineFor(res.status, error?.error, error?.field)})
                        return
                    }
                    const data = (await res.json()) as ContinueResponse
                    // Error-handling review F3: an unvalidated cast fed `mergeTake`, whose `withTake`
                    // (`useMine.ts`) reads `take.id` — a malformed 200 threw inside a React state
                    // updater, outside this `try`, and took the wall down. Same posture as a
                    // non-2xx: a `failed` notice, no state applied.
                    if (!isTake(data.take)) {
                        setNotice({key, line: errorLineFor(0)})
                        return
                    }
                    // The take IS the state: fold it into `/mine`'s own shape and every derived
                    // value — the cell's shimmer, the column's result, the pill — moves with it.
                    mergeTake(data.take)
                    track('wall_generate', {...generated, profile: profileRef.current || undefined})
                    touch(generated.tile_id)
                    if (!signedIn) track('free_pick', {house: theme.id, surface: 'wall', kind})
                    setDirection('')
                    // The take just asked for becomes the one on screen: the counter steps to its
                    // new last, and the ratio chip goes back to stating that take's own shape.
                    setSelectedStepId(null)
                    setRatioPick(null)
                    // The ghost has become a chain of its own; hold its root from here on (S4:
                    // never a second root for the same from-scratch line of work).
                    if (target.kind === 'ghost') {
                        setArmed((prev) =>
                            prev && armedChainKey(prev) === GHOST_CHAIN_KEY
                                ? {kind: 'scratch', rootId: data.take.root_id}
                                : prev,
                        )
                    }
                } catch {
                    setNotice({key, line: errorLineFor(0)})
                } finally {
                    setFiringFlag(key, false)
                    refetchMine()
                }
            })
        },
        [chains, mine, refetchMine, signedIn, freePickIsOpen, openSignIn, mergeTake, setAllowance, setFiringFlag, theme, gate, identity],
    )

    function handleGenerate() {
        // No model yet: the verb opens the intake, never `/continue`.
        if (identity.kind === 'model' && identity.state === 'missing' && renderIdentityIntake) {
            setIntakeOpen(true)
            return
        }
        // The step the user STEPPED to is the one continued from (BRIEF § Regenerate): a take
        // becomes the parent (or, with an empty field, the sibling's source); the basis is a fresh
        // twist of the tile itself. Not stepped, `from` stays null — `armedView.displayed` now also
        // stands for the chain's own in-flight row when nothing is selected (`chains.shownStep`), and
        // a running row must never become the parent of the next `/continue`: the server does not
        // check a parent's status, so a double-press mid-render would otherwise chain off a row that
        // may still fail. `steppedDisplay` carries only the deliberate selection.
        if (armed) void fire(armed, direction, ratioPick, armedView?.steppedDisplay ?? null)
    }

    // ── the pin door — beside the counter, in the column and the sheet alike ──────────────────
    /** `mergePinned` (the wall's cells, `/mine.pinned`) AND `recordTakes` (`takeLedger`, what the
     *  OPEN column's own door reads its state from — `armedView.pinnableTake`) both need this row,
     *  or the door the user is looking at keeps showing the state it had before the toggle even
     *  though the wall and `/mine` are already current (operator-verified defect, 2026-09-04). */
    const applyPinnedTake = useCallback((take: Take) => {
        mergePinned(take)
        setTakeLedger((prev) => recordTakes(prev, [take]))
    }, [mergePinned])

    /** Error-handling review F9: two rapid toggles of the same take issued a fetch each with no
     *  sequencing, so whichever reply arrived last won — even the STALE one, if the network
     *  reordered them. A monotonic token per take id, bumped on every press and compared before a
     *  reply is allowed to touch state: only the most recent press for a given take can still move
     *  it, and a superseded reply becomes a no-op rather than an out-of-order write. */
    const pinPressRef = useRef<Map<string, number>>(new Map())

    const togglePin = useCallback((take: Take) => {
        const on = take.pinned_at === null
        const previous = take
        const myPress = (pinPressRef.current.get(take.id) ?? 0) + 1
        pinPressRef.current.set(take.id, myPress)
        const isLatestPress = () => pinPressRef.current.get(take.id) === myPress
        // Optimistic: the cell appears or leaves on the NEXT render, the door fills or empties at
        // once — `applyPinnedTake` patches `pinned_at` in place wherever the row is held, never
        // reordering `mine.done`/`.pending` (`useMine.ts`'s own doc).
        applyPinnedTake({...take, pinned_at: on ? new Date().toISOString() : null})
        track(on ? 'pin' : 'unpin', {house: theme.id, chain: chainKeyOf(take)})
        void (async () => {
            try {
                const res = await fetch('/pin', {
                    method: 'POST',
                    headers: {'Content-Type': 'application/json'},
                    body: JSON.stringify({take: take.id, on}),
                })
                // Error-handling review F4: an expired session (the `/mine` poll is already logging
                // 401s by then, `useMine.ts`) used to fall through to the generic `!res.ok` branch —
                // "try again in a minute" for a press that will never succeed, when `errorLineFor`
                // already has the right word ('sign in first'). `fire`'s own 401 branch does the
                // same thing for `/continue`; this is that same route for the pin door.
                if (res.status === 401) {
                    if (isLatestPress()) {
                        applyPinnedTake(previous)
                        signInFor(take, GATE_TRIGGER_WALL)
                    }
                    return
                }
                if (!res.ok) {
                    if (isLatestPress()) {
                        applyPinnedTake(previous)
                        setNotice({key: chainKeyOf(take), line: PIN_ERROR_LINE})
                    }
                    return
                }
                const data = (await res.json()) as PinResponse
                // Error-handling review F3: an unvalidated cast fed `applyPinnedTake`, whose
                // `withPin` (`useMine.ts`) reads `take.id`/`.pinned_at` — a malformed 200 threw
                // inside a React state updater. Same posture as a non-2xx: roll back, show the
                // single pin-door line.
                if (!isTake(data.take)) {
                    if (isLatestPress()) {
                        applyPinnedTake(previous)
                        setNotice({key: chainKeyOf(take), line: PIN_ERROR_LINE})
                    }
                    return
                }
                if (isLatestPress()) applyPinnedTake(data.take)
            } catch {
                if (isLatestPress()) {
                    applyPinnedTake(previous)
                    setNotice({key: chainKeyOf(take), line: PIN_ERROR_LINE})
                }
            }
        })()
    }, [applyPinnedTake, signInFor, theme.id])

    // ── the return token — re-arm; a Render press that raised the card renders once (`render`) ──
    useEffect(() => {
        if (tokenReadRef.current) return
        tokenReadRef.current = true
        const hasToken = RETURN_TOKEN_KEYS.some((k) => searchParams.has(k))
        if (!hasToken) {
            if (arrivalNotice) setNotice({key: GHOST_CHAIN_KEY, line: arrivalNotice})
            return
        }

        // A closed house's visitor has nothing to re-arm (plan `2026_09_17_ovid_one_wall.md`
        // § Decided 2: a tap opens sign-in, it never arms), so a `?basis=` must not open a column on
        // their wall — the column names its basis, and naming an estate cell is the one thing a
        // visitor's page may not do. The picture asked for leads the house's page (the server read it
        // from the address) and the address is kept as it arrived, so the sign-in a later tap opens
        // returns to it (the picture armed, the search kept); nothing opens on arrival
        // (`2026_11_02_ovid_visitor_open.md` § Decided 5). The other keys are dropped.
        if (visitor) {
            const basis = searchParams.get('basis')
            if (basis === null || !/^\d+$/.test(basis)) {
                replaceUrl(strippedSearch(new URLSearchParams(window.location.search)))
            }
            return
        }

        const batchParam = searchParams.get('batch')
        const basisParam = searchParams.get('basis')
        const directionParam = searchParams.get('direction') ?? ''
        const ratioParam = searchParams.get('ratio') as RatioKey | null
        const renderParam = searchParams.get('render') === '1'

        // Read once, then strip — whatever the params turn out to say.
        replaceUrl(strippedSearch(new URLSearchParams(window.location.search)))

        // The page verb's landing (plan e): the batch's nine lead the wall, in position order, and
        // the pill speaks for them. NO COLUMN OPENS — the visitor asked the page a question and is
        // watching the answer arrive, so nothing is armed and nothing is fired. It wins over the
        // other keys because it is the only one a page verb writes.
        if (batchParam) {
            setArrivalBatchId(batchParam)
            return
        }

        // The pick is re-applied AFTER the arm below, which resets it: a ratio in the token is one
        // the user chose before the card, and it survives it (§10 — "the basis and the words
        // survive"; the ratio is now part of what they armed).
        const pick = ratioParam && (RATIO_KEYS as readonly string[]).includes(ratioParam) ? ratioParam : null

        if (basisParam !== null) {
            // S5: anything but a tile id is ignored outright — no arm, no column. Same for a tile
            // this product does not have (`GET /tile/:id` 404s on another house's id).
            if (!/^\d+$/.test(basisParam)) return
            const tileId = Number(basisParam)
            void (async () => {
                const tile = tileById(tileId) ?? (await fetchTile(tileId))
                if (!tile) {
                    // Error-handling review F7: after a sign-in round trip, `GET /tile/:id` failing
                    // (an unlogged 500 per F1, or a genuine 404 for a since-unlisted tile) used to
                    // drop the armed state silently — the params are already stripped above by the
                    // time this runs, so there was no second chance from the URL. Arms the ghost
                    // instead, keeping the words the visitor typed (§10's own rule: "the basis and
                    // the words survive the card") rather than losing both after the one flow the
                    // product most needs to survive, and says so once.
                    arm({kind: 'ghost'}, 'page', directionParam)
                    setNotice({key: GHOST_CHAIN_KEY, line: errorLineFor(0)})
                    if (pick) setRatioPick(pick)
                    return
                }
                arm({kind: 'tile', tileId}, 'page', directionParam)
                if (arrivalNotice) setNotice({key: tileChainKey(tileId), line: arrivalNotice})
                if (pick) setRatioPick(pick)
                if (renderParam) setRenderOnReturn(true)
            })()
            return
        }

        arm({kind: 'ghost'}, 'page', directionParam)
        if (arrivalNotice) setNotice({key: GHOST_CHAIN_KEY, line: arrivalNotice})
        if (pick) setRatioPick(pick)
        if (renderParam) setRenderOnReturn(true)
        // eslint-disable-next-line react-hooks/exhaustive-deps -- runs once against the mount URL only
    }, [])

    // Runs after the commit that armed the chain, so the verb reads the re-armed state (words, ratio).
    useEffect(() => {
        if (!renderOnReturn || !armed || !signedIn) return
        setRenderOnReturn(false)
        handleGenerate()
        // eslint-disable-next-line react-hooks/exhaustive-deps -- once, on the return that asked for it
    }, [renderOnReturn, armed, signedIn])

    // A gate is counted where it is met: a member opening a column whose verb cannot render (decision 8, plan
    // `2026_09_21_wall_gaps.md`). A disabled verb cannot be pressed, so a count at the press saw no one.
    const metGate = overlayOpen && signedIn && armed ? gateMet(mine?.identity, mine?.allowance?.remaining) : null
    const metChain = armed ? armedChainKey(armed) : null
    useEffect(() => {
        if (metGate) track('gate_hit', {gate: metGate, trigger: GATE_TRIGGER_WALL})
    }, [metGate, metChain])

    // The allowance's checkout comes back on the wall's own address (`HouseTheme.allowanceReturn`): read once, the
    // parameters stripped whatever they say, and a good return is welcomed until the next press. A return that
    // reopens the intake (`intake=1`) is the intake's own to read.
    const allowanceReturnReadRef = useRef(false)
    useEffect(() => {
        if (allowanceReturnReadRef.current) return
        allowanceReturnReadRef.current = true
        const back = theme.allowanceReturn
        if (!back || !signedIn || !searchParams.has(back.param) || searchParams.get('intake') === '1') return
        const value = searchParams.get(back.param) ?? ''
        const params = new URLSearchParams(window.location.search)
        params.delete(back.param)
        for (const key of back.alsoStrip) params.delete(key)
        replaceUrl(params)
        if (back.values.includes(value)) setAllowanceWelcome(true)
        // eslint-disable-next-line react-hooks/exhaustive-deps -- runs once against the mount URL only
    }, [])

    // A spent allowance frees when its next picture leaves the window: ask `/mine` again then, so the verb is back
    // without a reload.
    const allowanceResetsAt = mine?.allowance?.remaining === 0 ? mine.allowance.resetsAt : null
    useEffect(() => {
        if (!allowanceResetsAt) return
        const wait = Math.min(Math.max(Date.parse(allowanceResetsAt) - Date.now(), 0) + 1_000, MAX_TIMER_MS)
        const timer = setTimeout(refetchMine, wait)
        return () => clearTimeout(timer)
    }, [allowanceResetsAt, refetchMine])

    // `?arm=<tileId>&intake=1` — the pack page's gate verb: arms the tile and opens the intake, signed
    // in or not (the intake asks for sign-in at its own Start). Once, against the mount URL.
    const intakeParamReadRef = useRef(false)
    useEffect(() => {
        if (intakeParamReadRef.current) return
        intakeParamReadRef.current = true
        if (!renderIdentityIntake) return
        const armParam = searchParams.get('arm')
        const intakeParam = searchParams.get('intake')
        if (intakeParam !== '1') return
        const armedTile = armParam !== null && /^\d+$/.test(armParam)
        // `?intake=1` alone (Ovid, whose intake comes from the strip and returns to the wall as it stands) opens
        // the intake over the wall with nothing armed; a malformed `arm` is no tile and no reason to refuse.
        if (armParam !== null && !armedTile) return

        const params = new URLSearchParams(window.location.search)
        params.delete('arm')
        params.delete('intake')
        replaceUrl(params)

        if (!armedTile) {
            setIntakeOpen(true)
            return
        }
        const tileId = Number(armParam)
        void (async () => {
            const tile = tileById(tileId) ?? (await fetchTile(tileId))
            if (tile) arm({kind: 'tile', tileId}, 'page')
            setIntakeOpen(true)
        })()
        // eslint-disable-next-line react-hooks/exhaustive-deps -- runs once against the mount URL only
    }, [])

    // ── search ────────────────────────────────────────────────────────────────────────────────
    const onSearchChange = useCallback((value: string) => setQ(value), [])

    // The Make row, ⌘/Ctrl+Enter, or Enter at request length arms the ghost on the pills and the words, comma-joined
    // (`makeDirection`), ready to Generate: the column opens on the ghost exactly as a tap on it does, and Generate
    // stays behind the same sign-in gate (`fire`). The search keeps running beside it: `q` is untouched, so the wall
    // under the column stays the one the reader saw as they typed, and closing the column returns to it with the ghost
    // still carrying the words. A closed house's visitor has no column to arm (its ghost is the sign-in door).
    const onMake = useCallback((via: WallMakeVia) => {
        const direction = makeToArm(qRef.current, theme.facets, !!visitor)
        if (direction === null) return
        commitRecent(qRef.current)
        arm({kind: 'ghost'}, 'request', direction, {via})
    }, [visitor, theme.facets, arm, commitRecent])

    const onPick = useCallback((from: SearchFrom, next: string) => {
        searchFromRef.current = from
        setQ(next)
        settleNow()
    }, [settleNow])

    // A lean word removed: suppressed on the server, with a notice that takes it back.
    const onLeanDrop = useCallback((token: FieldToken) => {
        sendTouch({suppress: token.ref.value})
        setLeanNotice({token, line: droppedLine(token)})
        track('wall_lean_dropped', {house: theme.id, token: token.ref.value, profile: profileRef.current || undefined})
    }, [sendTouch, theme.id])
    const onLeanUndo = useCallback(() => {
        if (!leanNotice) return
        sendTouch({unsuppress: leanNotice.token.ref.value})
        setLeanNotice(null)
    }, [leanNotice, sendTouch])

    // A new query has not been reported yet — declared before the emitter so it resets first.
    useEffect(() => {
        searchFiredForRef.current = null
    }, [q])

    // S7: one `wall_search` per SETTLED query — after that query's own page has landed, carrying
    // that page's `matched_count`, never the previous query's. A visitor's settled query is the one
    // `useAnonPage` answers (a deep-linked `?q=` included); `signed_in` tells the two readers apart.
    const settledQuery = visitorAnswer ? visitorAnswer.query : wallPages.loadedQuery
    const settledCount = visitorAnswer ? visitorAnswer.page.tiles.length : (wallPages.matchedCount ?? 0)
    useEffect(() => {
        const due = searchToReport(fieldAnswer(q), {query: settledQuery, matchedCount: settledCount},
            searchFiredForRef.current, restoredQueryRef.current)
        if (!due) return
        searchFiredForRef.current = due.query
        track('wall_search', {
            query_len: due.query.length,
            matched_count: due.matchedCount,
            ghost_shown: true, // a non-empty query always puts the ghost on the wall (BRIEF §4)
            signed_in: signedIn,
            from: searchFromRef.current,
            profile: profileRef.current || undefined,
        })
        searchFromRef.current = 'typed'
    }, [settledQuery, settledCount, q, signedIn])

    /**
     * The wall paints the query it has an ANSWER for, never the one being typed (operator-verified
     * defect, 2026-08-31: the first keystroke moved the wall twice — the ghost was spliced into the
     * still-old page at once, re-justifying its row, and the debounced `/wall` answer then moved
     * everything again). `loadedQuery` is the query the tiles on screen answer, so the arrangement
     * changes exactly once, at the page swap: the ghost arrives with the tiles it belongs between,
     * and the arrival block leaves with them. The ghost's WORDS follow the field live — text inside
     * a cell of fixed ratio, which moves nothing.
     */
    const paintedQuery = wallPages.loadedQuery

    // ── the cells ──────────────────────────────────────────────────────────────────────────────
    /** What the pill's third branch counts as ready (`unseenDone`'s own doc) — read live, unlike
     *  placement: a take finishing within an entry says `N ready` on the pill while its cell holds. */
    const unseen = useMemo(() => unseenDone(mine), [mine])
    /** Every own chain's newest done row, as `/mine` sends it — live; the wall places from its
     *  snapshot (`Hold`) and reads this only for what is born within an entry. */
    const ownDone = useMemo(() => mine?.done ?? [], [mine])

    /** `/mine?q=`'s own `matched` — the user's chains found by `tsv @@ q` (direction and served
     *  prompt both), server-side (`2026_09_01_search_own_chains.md`). Empty whenever no search is
     *  running, since the field is then absent from the wire entirely. */
    const ownMatched = useMemo(() => mine?.matched ?? [], [mine])

    /** `/mine.pinned` — every own take with `pinnedAt` set, unfiltered by `q` (`contract.ts`'s own
     *  doc; plan `2026_09_04_wall_pin.md`). `cells.ts` places one cell per entry, skipped when it is
     *  currently its chain's face. */
    const ownPinned = useMemo(() => mine?.pinned ?? [], [mine])

    const ownByTile = useMemo(() => mine?.byTile ?? [], [mine])

    // A matched chain rooted on a tile off every loaded page has nothing to paint until its own
    // tile lands — the same `GET /tile/:id` fetch the armed basis step already uses (`ensureTile`
    // is idempotent: a tile already known, or already requested, is a no-op).
    useEffect(() => {
        for (const take of ownMatched) {
            if (take.root_tile_id !== null) ensureTile(take.root_tile_id)
        }
    }, [ownMatched, ensureTile])

    // A pinned take rooted on a tile off every loaded page — same fetch, same reason.
    useEffect(() => {
        for (const take of ownPinned) {
            if (take.root_tile_id !== null) ensureTile(take.root_tile_id)
        }
    }, [ownPinned, ensureTile])

    /** `/mine?q=`'s matched block, once it answers the query on screen — until then the response
     *  still carries the previous query's matches, which name nothing this page is about. */
    const ownMatchedNow = answeredQuery === paintedQuery ? ownMatched : []

    /**
     * The entries (plan `2026_09_26_wall_hold_still_pill.md` § The rule): sign-in or sign-out, a
     * change of the painted query, the first `/mine?q=` answer for it, a `?batch=` landing — and the
     * page load, which is the first key. Nothing else reorders the wall: a poll, a take reaching
     * `done`, a `/seen` stamp, a catalog page appending (re-placed below by the same function).
     */
    const entryKey = [
        mine ? 'mine' : 'none',
        paintedQuery,
        paintedQuery && answeredQuery !== paintedQuery ? 'asked' : 'answered',
        arrivalBatchId ?? '',
    ].join('|')
    const takeHold = (now: number): Hold => ({
        key: entryKey,
        now,
        ownDone,
        ownMatched: ownMatchedNow,
        ownPinned,
        batch: batchTakes(mine, arrivalBatchId),
        born: [],
    })
    const [hold, setHold] = useState<Hold>(() => takeHold(nowRef.current))
    // React's own "adjust state while rendering" idiom: the render that crosses an entry is
    // replaced at once by one painted from the fresh snapshot, before anything is committed.
    if (hold.key !== entryKey) setHold(takeHold(Date.now()))
    /** The one live subtraction: a pin removed within the entry takes its cell with it. */
    const kept = useMemo(() => withoutUnpinned(hold, ownPinned), [hold, ownPinned])

    /**
     * What the wall paints, and in what order — one pure function (`cells.ts`), specified there:
     * the ghost first, then own chains by day, then `default_rank` at rest; the wire's order with
     * the matching scratch takes and the ghost first under a query. Fed the held snapshot, so a
     * poll moves nothing; the born cells ride after the ghost. `arrivalTakeIds` is what `POST
     * /seen` stamps, `arrivalCellCount` what `wall_arrival_seen` reports.
     */
    const painted = useMemo(
        () => visitorPage ? anonCells(visitorPage) : wallCells({
            tiles: wallPages.tiles,
            seamAt: wallPages.seamAt,
            query: paintedQuery,
            now: hold.now,
            ownDone: hold.ownDone,
            ownMatched: hold.ownMatched,
            ownPinned: kept.ownPinned,
            ownByTile,
            tileById,
            batch: hold.batch,
            born: kept.born,
        }),
        [visitorPage, wallPages.tiles, wallPages.seamAt, paintedQuery, hold, kept, ownByTile, tileById],
    )

    // Within an entry, whatever the live `/mine` holds that has no cell yet is born after the ghost
    // and kept there (`cells.bornTakes`); the next render paints it, which empties this list.
    const bornNow = useMemo(
        () => anonPage ? [] : bornTakes({
            painted: painted.cells, query: paintedQuery, ownDone, ownMatched: ownMatchedNow, ownPinned,
            heldPinned: kept.ownPinned, tileById,
        }),
        // `ownMatchedNow` is derived from `ownMatched`, `answeredQuery` and `paintedQuery`, all listed.
        // eslint-disable-next-line react-hooks/exhaustive-deps
        [anonPage, painted, paintedQuery, ownDone, ownMatched, answeredQuery, ownPinned, kept.ownPinned, tileById],
    )
    useEffect(() => {
        if (bornNow.length === 0) return
        setHold((h) => (h.key === entryKey ? {...h, born: [...bornNow, ...h.born]} : h))
    }, [bornNow, entryKey])
    const cells = painted.cells
    /** Where the visitor's coda begins (`cells.anonCells`) — absent on every open wall, which has
     *  no end to bound. */
    const codaFrom = painted.codaFrom ?? null

    const rows = useMemo(() => {
        const all = justifyRows(cells, containerWidth, theme.targetRowHeight, theme.gap)
        // The wall ends once (plan § Decided 5): a bounded coda and no ragged row, decided on the
        // rows the measured container actually produced, so the ending is the same depth at every
        // breakpoint rather than the same number of tiles.
        return visitor && codaFrom !== null
            ? boundedRows(all, {codaFrom, codaRows: visitor.codaRows})
            : all
    }, [cells, containerWidth, theme.targetRowHeight, theme.gap, visitor, codaFrom])

    // ── /seen — one caller for the stamp, two reasons to run it ───────────────────────────────
    /** Returns how many rows the server stamped, or `null` when the request did not land at all —
     *  a 4xx/5xx as much as a dead connection. Nothing is recorded as stamped on a `null` (N5/N10):
     *  the ids stay unstamped, so the next pass posts them again and the row's own state is still
     *  the truth in the meantime. */
    const stampSeen = useCallback(async (ids: string[]): Promise<number | null> => {
        try {
            const res = await fetch('/seen', {
                method: 'POST',
                headers: {'Content-Type': 'application/json'},
                body: JSON.stringify({ids}),
            })
            if (!res.ok) return null
            const data = (await res.json()) as SeenResponse
            return typeof data?.stamped === 'number' ? data.stamped : ids.length
        } catch {
            return null
        }
    }, [])

    // The arrival set, once it has been painted at rest with an empty query (B3). The lift itself
    // is held (N1), so this stamp changes nothing on screen — it only records the visit.
    const arrivalKey = painted.arrivalTakeIds.join(',')
    const arrivalIdsRef = useRef<string[]>(painted.arrivalTakeIds)
    arrivalIdsRef.current = painted.arrivalTakeIds
    /** What `wall_arrival_seen` counts: the cells that arrived, one per chain — not the rows the
     *  stamp happened to touch. */
    const arrivalCellsRef = useRef<number>(painted.arrivalCellCount)
    arrivalCellsRef.current = painted.arrivalCellCount
    useEffect(() => {
        if (!owns) return
        const ids = arrivalIdsRef.current.filter((id) => !seenPostedRef.current.has(id)).slice(0, MAX_SEEN_IDS)
        if (ids.length === 0) return
        const cellCount = arrivalCellsRef.current
        const timer = setTimeout(async () => {
            if (seenInFlight.current) return
            seenInFlight.current = true
            const stamped = await stampSeen(ids)
            seenInFlight.current = false
            // N10: an event for a stamp that never happened is a lie about the user's session. The
            // rows lift again on the next visit, and `wall_arrival_seen` fires then instead.
            if (stamped === null) return
            ids.forEach((id) => seenPostedRef.current.add(id))
            if (stamped > 0) track('wall_arrival_seen', {n_unseen: cellCount})
            refetchMine()
        }, ARRIVAL_PAINT_MS)
        return () => clearTimeout(timer)
    }, [arrivalKey, owns, refetchMine, stampSeen])

    // ── a failed row: said once, stamped when the stamp lands, and then the queue empties ──────
    const failedUnseenRows = useMemo(
        () => (mine?.pending ?? []).filter((p) => p.status === 'failed' && p.seen_at === null),
        [mine],
    )
    useEffect(() => {
        if (!owns) return
        const fresh = failedUnseenRows.filter((r) => !failedNotedRef.current.has(r.id))
        for (const row of fresh) {
            failedNotedRef.current.add(row.id)
            track('wall_generation_failed', {reason: row.fail_reason ?? 'unknown'})
        }
        if (fresh.length > 0) setNotice({key: chainKeyOf(fresh[0]), line: failedLineFor(fresh[0], theme)})

        // N5: the stamp is what takes the row out of `pending`, and `pending` is what keeps the
        // poll alive — so a stamp marked as done when it never landed left the row pending for
        // good, polling every ten seconds with nothing left to learn. Marked on a 2xx only, and
        // retried by the next poll's own run of this effect; the event above is not repeated.
        const ids = failedUnseenRows
            .filter((r) => !seenPostedRef.current.has(r.id))
            .map((r) => r.id)
            .slice(0, MAX_SEEN_IDS)
        if (ids.length === 0 || seenInFlight.current) return
        seenInFlight.current = true
        void (async () => {
            const stamped = await stampSeen(ids)
            seenInFlight.current = false
            if (stamped === null) return
            ids.forEach((id) => seenPostedRef.current.add(id))
            refetchMine()
        })()
    }, [failedUnseenRows, owns, refetchMine, stampSeen])

    useEffect(() => {
        if (!notice) return
        const timer = setTimeout(() => setNotice(null), NOTICE_MS)
        return () => clearTimeout(timer)
    }, [notice])

    // ── windowing — rows outside the viewport ± 2 screens render as a height-only spacer ───────
    const windowTop = scrollTop - 2 * viewportH
    const windowBottom = scrollTop + 3 * viewportH

    // A wall that has answered a new query crossfades what changed: the previous layout's pictures fade out over it
    // (`SwapFade.tsx`), so a cell that keeps its place is not seen to move.
    const swapping = useSwapFade(settledQuery, () =>
        fadeRects(
            rows,
            (cell) => cell.kind === 'tile'
                ? theme.tileImageUrl(cell.tile.image_key)
                : cell.kind === 'anon' ? ('image_key' in cell.tile ? theme.tileImageUrl(cell.tile.image_key) : cell.tile.veil) : null,
            theme.gap,
            (top, bottom) => wallTopRef.current + bottom >= windowTop && wallTopRef.current + top <= windowBottom,
        ))

    // ── sentinel — fetch the next cursor page as the user nears the bottom ─────────────────────
    // Two viewports ahead (2026-09-23): the fixed 600px was a third of a desktop screen and a
    // single wheel flick outran it.
    const sentinelRef = useRef<HTMLDivElement>(null)
    useEffect(() => {
        const el = sentinelRef.current
        if (!el || !wallPages.hasMore) return
        const observer = new IntersectionObserver(
            (entries) => {
                if (entries[0]?.isIntersecting) wallPages.loadMore()
            },
            {rootMargin: '200% 0px'},
        )
        observer.observe(el)
        return () => observer.disconnect()
    }, [wallPages.hasMore, wallPages.loadMore])

    // ── pill dismissal (sessionStorage, amendment N6) ──────────────────────────────────────────
    // S9: dismissed for the tab — but a reload with work still in the queue brings it back, since
    // the pill is the only way back to that work.
    useEffect(() => {
        try {
            const stored = window.sessionStorage.getItem(PILL_DISMISSED_KEY)
            if (!stored) return
            const stillRunning = (initialMine?.pending ?? []).some(
                (p) => p.status === 'queued' || p.status === 'rendering',
            )
            if (stillRunning) window.sessionStorage.removeItem(PILL_DISMISSED_KEY)
            else setPillDismissedAt(Number(stored))
        } catch {}
        // eslint-disable-next-line react-hooks/exhaustive-deps -- the mount-time snapshot only
    }, [])

    function dismissPill() {
        const now = Date.now()
        try {
            window.sessionStorage.setItem(PILL_DISMISSED_KEY, String(now))
        } catch {}
        setPillDismissedAt(now)
        setNotice(null)
        track('pill_dismiss', {})
    }

    // ── everything the column shows, derived from the armed chain ─────────────────────────────
    const armedView = useMemo(() => {
        if (!armed) return null
        const key = armedChainKey(armed)
        const chain = chains.get(key)
        // The counter and the step it points at (BRIEF § The movement). With nothing selected
        // the chain's tail is shown and the column behaves exactly as it did before travel
        // existed:
        // the chain's own working state drives the shimmer, so a Generate lights it in one frame,
        // before any row exists to select.
        const basisTile = armed.kind === 'tile' ? tileById(armed.tileId) : null
        // The chain as the counter walks it: the basis tile the chain was twisted from, then its
        // takes (`chainSteps`). A from-scratch chain has no basis and its first take is step 1.
        const steps = chainSteps(chainHistory(takeLedger, key), basisTile)
        const picked = displayedStep(steps, selectedStepId)
        const stepped = selectedStepId !== null && picked?.id === selectedStepId
        // Not stepped, the column steps to the chain's own in-flight row, else its newest done row
        // (`chains.shownStep`: the counter must read `N+1` the instant a Generate is accepted,
        // never stay pinned to the done row one place behind it while the frame shimmers).
        const shown = shownStep(picked, stepped, chain)
        const shownTake = shown?.kind === 'take' ? shown.take : null
        // On the basis nothing is being awaited: the tile's picture is already there.
        const working = stepped
            ? shownTake !== null && shownTake.status !== 'done'
            : (!!chain?.running || firing.has(key))
        // The cell at work's two paint inputs (`Tile.tsx`'s/`WorkingCell.tsx`'s own header docs) —
        // the same row `working` above is read off: stepped back to an in-flight take, that take
        // itself (`chainHistory` never carries a `failed` row into `steps`, so a non-`done` shownTake
        // here is always queued or rendering); not stepped, the chain's own running row. `null` while
        // merely `firing` (optimistically working before `mine` has even shown the row yet), which
        // the shared cell paints as the breath alone — same fallback the tile's own render site uses.
        const workingRow = stepped ? (working ? shownTake : null) : (chain?.running ?? null)
        const queuePlace = workingRow?.status === 'queued'
            ? (queuedOrder.get(workingRow.id) ?? null)
            : workingRow?.status === 'rendering' ? 1 : null
        const previewFrame = workingRow?.status === 'rendering'
            ? (previewFrames.get(workingRow.id) ?? null)
            : null
        const takeUrl = shownTake?.image_key ? theme.takeImageUrl(shownTake.image_key) : null
        // N14 + the 2026-08-31 ratio decision: the frame states the ratio of the picture that is
        // coming — the row being made now, else the pick, else the shape of the step on screen.
        // Stepped back, the frame holds the picture it is showing (the basis tile's own dimensions
        // included): it must not be fitted to another row's ratio while the user is looking at it.
        const makingNow = stepped ? null : (chain?.running ?? null)
        const aspect = armedAspect({
            armed, chains, basis: basisTile, displayed: shown, inFlight: makingNow, ratioPick,
            houseRatio: theme.defaultRatio,
        })
        const common = {
            steps,
            displayed: shown,
            // The deliberate selection only — `null` when the counter is simply following the
            // chain (`shown` may be the in-flight row in that case). `handleGenerate` reads this,
            // never `displayed`, for what the next `/continue` is parented on.
            steppedDisplay: stepped ? picked : null,
            working,
            queuePlace,
            previewFrame,
            aspect,
            ratio: effectiveRatio(ratioPick, aspect, theme.ratioKeys),
            // The pin door's own take (plan `2026_09_04_wall_pin.md`) — rendered only when the
            // displayed step is a DONE take: never on the basis step (`shown?.kind === 'basis'`, no
            // take at all), never on the ghost's blank state (`shown === null`), never on a queued
            // or rendering row (edge case 6 — a pin can only ever be a picture that exists).
            pinnableTake: shownTake && shownTake.status === 'done' ? shownTake : null,
            // The Share door's picture: the done take on screen, else the basis tile when it is the step.
            sharePicture: shownTake ? takePicture(theme, shownTake)
                : shown?.kind === 'basis' ? tilePicture(theme, shown.tile) : null,
            // The pin SLOT's own state (`Counter.tsx`'s `PinSlotState`, dev fix 2026-09-18) — signed-
            // in-ness is folded in below, at `pinSlotState`, not here: this is purely "can the step on
            // screen ever be pinned," the same three cases `pinnableTake`'s own doc states, restated
            // as a tri-state rather than a nullable take. `shown === null` (the ghost's still-blank
            // state) reads `'none'` too, though `Counter` never renders there regardless (`total < 2`).
            pinSlot: (
                shown === null ? 'none'
                    : shown.kind === 'basis' ? 'idle'
                        : shownTake?.status === 'done' ? 'live'
                            : 'idle'
            ) as PinSlotState,
        }

        if (armed.kind === 'ghost') {
            return {
                ...common,
                rootLabel: theme.verbs.blankBasisLabel,
                rootDoors: captionDoors(null, null, pills),
                rootHref: null,
                resultUrl: takeUrl,
                isScratch: true,
            }
        }
        if (armed.kind === 'scratch') {
            return {
                ...common,
                // A from-scratch chain has no basis step, so the step on screen is always a take.
                rootLabel: shownTake?.direction || theme.verbs.blankBasisLabel,
                rootDoors: captionDoors(null, shownTake?.direction, pills),
                rootHref: null,
                resultUrl: takeUrl,
                isScratch: false,
            }
        }
        const tileUrl = basisTile ? theme.tileImageUrl(basisTile.image_key) : null
        return {
            ...common,
            // BRIEF §5: the basis large — the tile's own picture until a take replaces it.
            rootLabel: basisTile ? tileLabel(basisTile) : theme.verbs.blankBasisLabel,
            // Board `WordsAsDoors`: the basis tile's own chips, then the take on screen's typed
            // direction as one more door.
            rootDoors: captionDoors(basisTile, shownTake?.direction, pills),
            // The pack page behind the basis — a house decision (`theme.tileLabelHref`), the same
            // door the tile's own hover label opens; `null` where the house has no page (Ovid).
            rootHref: basisTile ? theme.tileLabelHref(basisTile.pack_slug) : null,
            resultUrl: takeUrl ?? tileUrl,
            isScratch: false,
        }
    }, [armed, chains, firing, previewFrames, queuedOrder, ratioPick, selectedStepId, takeLedger, theme, tileById, pills])

    // ── the verb's word — Generate vs Again, held steady across a render (chains.holdVerb) ──────
    /**
     * `verbLabel` (a pure readout of the step on screen, the trimmed field, and — Ovid — whether
     * `identity` still agrees with the take on screen, `chains.museMatches`) run through `holdVerb`,
     * which is the rule stated in full there: live except while the displayed step is still
     * rendering, where it keeps what it read when the render started; a fresh `arm()` always gets
     * its own live word regardless.
     *
     * Refs, not state: this needs to read correctly in the SAME render that computed it (Column and
     * Sheet below), and mutating during render — comparing the previous `armed` identity, exactly
     * the React-documented pattern for resetting derived state when an identity changes — needs no
     * extra render of its own the way an effect would.
     */
    const liveVerb = armedView ? verbLabel(armedView.displayed, direction, identity) : 'generate'
    const heldVerbRef = useRef<VerbState>(liveVerb)
    const heldVerbArmedRef = useRef<Armed | null>(null)
    const freshArm = armed !== heldVerbArmedRef.current
    heldVerbArmedRef.current = armed
    heldVerbRef.current = holdVerb({
        live: liveVerb, working: !!armedView?.working, held: heldVerbRef.current, freshArm,
    })
    const verbState = heldVerbRef.current

    // ── the pill: the same chain values, folded (BRIEF §11) ───────────────────────────────────
    const inFlight = useMemo(() => rowInFlight(mine), [mine])
    const runningCount = useMemo(
        () => (mine?.pending ?? []).filter((p) => p.status === 'queued' || p.status === 'rendering').length,
        [mine],
    )

    const pill = useMemo(() => {
        let key: ChainKey | null = null
        let line: string | null = null
        /** The row the pill speaks for, when it speaks for work rather than for a notice — the one
         *  whose batch, if it has one, names the pill. */
        let spokenFor: typeof inFlight = null
        let ready = false
        if (notice) {
            key = notice.key
            line = notice.line
        } else if (inFlight) {
            spokenFor = inFlight
            key = chainKeyOf(inFlight)
            // While the model trains, a queued row waits on the training, not the render queue.
            if (identity.kind === 'model' && identity.state === 'training') {
                const trainEtaMin = identity.eta_ms ? Math.ceil(identity.eta_ms / 60_000) : null
                line = trainEtaMin ? `training · ~${trainEtaMin} min` : 'training…'
            } else {
                const etaMin = mine?.eta_ms ? Math.ceil(mine.eta_ms / 60_000) : null
                line = runningCount > 1 && etaMin ? `generating · ~${etaMin} min` : 'generating…'
            }
        } else if (unseen.length > 0) {
            spokenFor = unseen[0]
            key = chainKeyOf(unseen[0])
            // Counted in CELLS, like the arrival block it announces (BRIEF § Where a take lives):
            // two takes of one chain are one picture waiting on the wall, not two.
            line = `${collapseChains(unseen).length} ready`
            ready = true
        } else {
            // Iteration II §3: "the pill is present from the first chain until ×" — the fourth and
            // last branch, once nothing is happening and nothing is unseen. It speaks for the
            // user's newest chain whatever its state, with no line: there is nothing to report,
            // only somewhere to return to (`chains.newestOwnRow`'s own doc).
            const anyOwn = newestOwnRow(mine)
            if (anyOwn) {
                spokenFor = anyOwn
                key = chainKeyOf(anyOwn)
                line = null
            }
        }
        if (key === null) return null

        const chain = chains.get(key) ?? null
        const running = chain?.running ?? null
        const newest = chain?.newestDone ?? null
        const rootTile = chain && chain.rootTileId !== null ? tileById(chain.rootTileId) : null
        // A page verb's nine are ONE piece of work, so the pill names the pack and, until a take
        // lands, shows its cover rather than whichever of the nine reached the head of the queue.
        const cover = spokenFor?.batch_id ? batchCover(batchTakes(mine, spokenFor.batch_id)) : null

        // One slot (BRIEF § Docked, 2026-09-26): the newest take at its own ratio, else the root —
        // the pack cover, the tile's original, or nothing for a blank, sized by the pending row.
        const rootPicture = cover ?? rootTile
        const slot: Pick<PillProps, 'slotUrl' | 'slotWidth' | 'slotHeight' | 'working'> = {
            ...(newest?.image_key
                ? {slotUrl: theme.takeImageUrl(newest.image_key), slotWidth: newest.width, slotHeight: newest.height}
                : rootPicture
                    ? {slotUrl: theme.tileImageUrl(rootPicture.image_key), slotWidth: rootPicture.width, slotHeight: rootPicture.height}
                    : {slotUrl: null, slotWidth: running?.width ?? 2, slotHeight: running?.height ?? 3}),
            working: running && (running.status === 'queued' || running.status === 'rendering')
                ? {
                    status: running.status,
                    previewFrame: running.status === 'rendering' ? (previewFrames.get(running.id) ?? null) : null,
                }
                : null,
        }

        const target: Armed | null = chain
            ? chain.rootTileId !== null
                ? {kind: 'tile', tileId: chain.rootTileId}
                : {kind: 'scratch', rootId: chain.rootId}
            : null

        return {
            // The pack's own name for a batch — the tile's chips (reading · style) name a cell, and
            // a cell is not what is being made.
            rootLabel: cover
                ? cover.title
                : rootTile
                    ? tileLabel(rootTile)
                    : newest?.direction || running?.direction || theme.verbs.blankBasisLabel,
            ...slot,
            ready,
            line,
            live: running?.status === 'rendering',
            target,
            // C3: `null` for a single (Continue ↑ always opens the column); a batch's id lets
            // `handlePillContinue` decide between opening the column and scrolling to the nine.
            batchId: spokenFor?.batch_id ?? null,
        }
    }, [notice, inFlight, unseen, chains, mine, runningCount, theme, tileById, identity, previewFrames])

    const pillLatestActivity = useMemo(() => {
        const all = [...(mine?.pending ?? []), ...(mine?.done ?? [])]
        return all.reduce((max, row) => Math.max(max, new Date(row.created_at).getTime()), 0)
    }, [mine])

    // Failure-logging review finding 1, amended: raising the pill while an overlay is open does not
    // work — the pill sits at a lower stacking layer than the column/sheet scrim (`--tl-z-pill`
    // below `--tl-z-arena`, `products/toile/styles/index.scss`), which also takes pointer events, so
    // the earlier fix (showing the pill whenever a `notice` exists) rendered it neither visible nor
    // reachable. The pill goes back to hiding outright behind an open overlay, matching what its
    // stacking layer already enforces; `noteForOverlay` (`ui/chains.ts`) is where the notice
    // actually surfaces instead.
    const showPill = !!pill && !overlayOpen
        && !(pillDismissedAt !== null && pillLatestActivity <= pillDismissedAt)

    /**
     * N4, amended (B1): `notice` is keyed on the chain it happened to (`{key, line}`) — a refused
     * `/continue` on one tile, a `failed` row on another. While an overlay is open it is the ONE
     * surface on screen, so its note slot carries any notice at all, not only one scoped to the
     * armed chain — "a failure on another chain is visible" has nowhere else to land once the pill
     * itself is hidden behind the scrim (see `showPill` above and `noteForOverlay`'s own doc).
     */
    const armedNote = noteForOverlay(overlayOpen, notice)
    const allowanceBar = overlayOpen && signedIn && mine?.allowance && theme.allowanceBar
        ? theme.allowanceBar(mine.allowance, {
              welcome: allowanceWelcome,
              returnTo: armed ? returnTokenFor(armed, '', null) : '/',
          })
        : null
    const openIntake = renderIdentityIntake ? () => setIntakeOpen(true) : undefined

    /** The pin door's props for the counter (`Counter.tsx`), or `null` to render nothing — signed
     *  in, and a done take on screen (`armedView.pinnableTake`'s own doc states the rest of the
     *  gate; `signedIn` here is belt-and-braces for edge case 8, "anonymous → no door"). */
    const pinDoor = armedView?.pinnableTake && owns
        ? {
            pinned: armedView.pinnableTake.pinned_at !== null,
            // A visitor's free take cannot be pinned without an account: the door asks for one.
            onToggle: () => (signedIn
                ? togglePin(armedView.pinnableTake!)
                : signInFor(armedView.pinnableTake!, GATE_TRIGGER_WALL)),
        }
        : null

    /** The Share door's props for the frame (`useShare.ts`), or `null` to render nothing — on every picture
     *  on screen, a take or the basis tile. A 401 on a take's stamp opens sign-in the way the pin's does,
     *  returning to the armed chain by the return token. */
    const shareDoor = useShare({
        theme, signedIn, picture: armedView?.sharePicture ?? null, setNotice,
        openSignIn: (take) => signInFor(take, GATE_TRIGGER_SHARE),
    })

    /** The pin slot's own state for the counter (`Counter.tsx`'s `PinSlotState`) — signed out reads
     *  `'none'` regardless of what the step on screen could otherwise support (edge case 8,
     *  restated for the slot the same way `pinDoor`'s own comment states it for the door). */
    const pinSlotState: PinSlotState = owns ? (armedView?.pinSlot ?? 'none') : 'none'

    /** The top of the row holding a cell, in document coordinates — the same running sum the render
     *  loop below builds (`cumTop`), read on demand rather than kept in a ref of its own. `null`
     *  when the cell is not in the CURRENT ordering: a query that does not reach it, or a chain
     *  whose basis tile is on a page not yet loaded. Never fetches a page to look further (Iteration
     *  II §3, plan § The pill that stays: "do not fetch pages to find it"). */
    function rowTopForCellKey(key: string): number | null {
        let top = wallTopRef.current
        for (const row of rows) {
            if (row.cells.some(({tile}) => tile.key === key)) return top
            top += row.height + theme.gap
        }
        return null
    }

    function handlePillContinue() {
        track('pill_continue', {})
        // S8: the pill always names a chain, so Continue always opens the column ON one — tile
        // chains and from-scratch chains alike, whether or not the tile is on a loaded page.
        if (!pill?.target) return
        // C3 (BRIEF § Docked): during a batch, before any of its nine cells has finished, there is
        // no take yet for the column to open on — Continue ↑ instead returns to where the batch's
        // cells sit, the top of the wall (the day-0 placement lifts pending rows there).
        if (pillContinueAction(mine, pill.batchId) === 'scroll_to_top') {
            window.scrollTo({top: 0, behavior: 'smooth'})
            return
        }
        const target = pill.target
        // Iteration II §3: Continue ↑ scrolls the wall to the chain's cell, THEN arms — the column
        // is what the user sees, the scroll is what the return (folding it back into the pill) buys
        // them. Not found in the current ordering, it arms without scrolling — there is nowhere on
        // screen to scroll to. The pill always names a chain, so the target is never the ghost.
        const top = rowTopForCellKey(cellKeyForChain(target))
        // Scroll BEFORE `arm()`: the scroll-return listener attaches only once `overlayOpen` is
        // true (which `arm()` sets) and watches the gesture (`wheel`/`touchmove`), never
        // `window.scrollY`, so a programmatic `scrollTo` cannot trip the fold in either order.
        if (top !== null) window.scrollTo({top, behavior: 'smooth'})
        arm(target, 'tap')
    }

    // ── the sign-in overlay: one layer, focus trapped, Escape closes it ────────────────────────
    // The trap this overlay shipped with now lives in `useFocusTrap`, where the column and the
    // sheet use it too (N8) — one implementation, so the three layers cannot drift apart.
    // Focus opens on the LAYER, not on its first control — the same `initialFocusRef` the sheet
    // uses to keep the phone keyboard down. The card's first focusable is its Google submit button
    // (`SignInCard`), and a gate that opens with the OAuth bounce under the keyboard is one stray
    // Enter or Space from leaving for Google before the card has been read (§10: the card presents
    // its options; Google goes on its button's own click). Focusing the dialog is also what the
    // ARIA authoring practices ask for when the first control carries consequences.
    /**
     * A visitor's one gesture (plan § Decided 2/3/4): a tap on any cell of a closed house's wall,
     * and on the ghost's door, opens the sign-in card rather than arming the column — the column
     * would show the estate's own words, which is exactly what a visitor may not see. The tapped
     * cell rides the return token (`HouseTheme.visitor.returnTo`) so the house can pick the page up
     * where the reader left it once the round-trip lands.
     */
    const openVisitorSignIn = useCallback((tileId: number | null, render = false) => {
        if (!visitor) return
        // A door that names no tile (the header's, the ghost's) still returns to the picture the visitor arrived on.
        const arrived = new URLSearchParams(window.location.search).get('basis')
        const basis = tileId ?? (arrived !== null && /^\d+$/.test(arrived) ? Number(arrived) : null)
        openSignIn(withWallQuery(visitor.returnTo(basis, render), q))
    }, [visitor, q, openSignIn])

    const closeSignIn = useCallback(() => setSignInOpen(false), [])
    useFocusTrap({
        active: signInOpen, containerRef: signInRef, onEscape: closeSignIn, initialFocusRef: signInRef,
    })

    // ── the search field's steering (a closed house's visitor has a plain field: nothing is shown of what it leans to) ──
    const steer: SearchSteer | undefined = visitor ? undefined : {
        house: theme.id,
        facets: theme.facets,
        shares: FACET_SHARES[theme.id] ?? {},
        facetOffer,
        lean: leanOf(profileKey, theme.facets),
        recent,
        // What sits with the pills: the chips of the matched tiles on screen, so nothing is asked of the server. A chip
        // that names a token already applied (a facet pill reads as its short label) is not offered again.
        withThese: yours.length > 0 ? withThese(wallPages.tiles, matchedOnScreen(wallPages.tiles.length, wallPages.seamAt, wallPages.loadedQuery), yours.map((t) => t.label)) : [],
        notice: leanNotice ? {line: leanNotice.line, onUndo: onLeanUndo} : null,
        landed,
        onPick,
        onLeanDrop,
        onRecentClear: () => { setRecent([]); writeRecent(browserStore(), theme.id, []) },
        onRecentRemove: (at) => setRecent((prev) => {
            const next = removeRecent(prev, at)
            writeRecent(browserStore(), theme.id, next)
            return next
        }),
        onMake,
        onCommit: (settled) => {
            commitRecent(settled)
            settleNow()
        },
    }

    // ── rendering ───────────────────────────────────────────────────────────────────────────────
    return (
        <div className={styles.page}>
            {renderHeader(
                {
                    value: q, onChange: onSearchChange, onPillRemove,
                    placeholder: theme.verbs.placeholder.onBlank, label: chromeWords(theme).search,
                    steer,
                },
                visitor ? () => openVisitorSignIn(null) : null,
            )}

            <div className={styles.wall} ref={wallRef}>
                {swapping && <SwapFade key={swapping.swap} rects={swapping.rects} />}
                {(() => {
                    let cumTop = 0
                    return rows.map((row, rowIndex) => {
                        const rowTop = wallTopRef.current + cumTop
                        const rowBottom = rowTop + row.height
                        cumTop += row.height + theme.gap
                        const visible = rowBottom >= windowTop && rowTop <= windowBottom

                        // Keyed by the row's first cell, never by its index (N12): a cell that
                        // changes row — the arrival block lifting, a search re-ordering — used to
                        // remount every Tile below it, restarting the image fade and the shimmer.
                        const rowKey = row.cells[0]?.tile.key ?? `row${rowIndex}`

                        if (!visible) {
                            return <div key={rowKey} style={{height: row.height}} />
                        }

                        return (
                            <div className={styles.row} style={{height: row.height}} key={rowKey}>
                                {row.cells.map(({tile: cell, width}) => {
                                    if (cell.kind === 'ghost') {
                                        return (
                                            <div key={cell.key} style={{width}}>
                                                <Ghost
                                                    words={ghostDirection}
                                                    atRest={ghostWords(theme).atRest}
                                                    onTap={() => {
                                                        arm({kind: 'ghost'}, 'ghost', ghostDirection)
                                                        track('ghost_tapped', {})
                                                    }}
                                                />
                                            </div>
                                        )
                                    }

                                    // A visitor's cell — the shared tile, with the house's door and
                                    // no page behind it. `'image_key' in cell.tile` is the wire
                                    // union's own discriminant (`contract.ts`'s `AnonTile`): the
                                    // veiled arm has no picture key to read, which is what makes
                                    // "no estate text on a visitor's tile" a fact about the type.
                                    if (cell.kind === 'anon') {
                                        if (!visitor) return null
                                        const anon = cell.tile
                                        const open = 'image_key' in anon
                                        const arrived = searchParams.get('basis') === String(anon.id)
                                        return (
                                            <div key={cell.key} style={{width}} className={arrived ? styles.arrived : undefined}>
                                                <Tile
                                                    imageUrl={'image_key' in anon
                                                        ? theme.tileImageUrl(anon.image_key)
                                                        : anon.veil}
                                                    label={open ? visitor.anonLabel.open : visitor.anonLabel.veiled}
                                                    labelHref={null}
                                                    door={open ? visitor.anonDoor.open : visitor.anonDoor.veiled}
                                                    state="rest"
                                                    width={cell.width}
                                                    height={cell.height}
                                                    eager={rowIndex === 0 || measured}
                                                    // Plan P5: an OPEN cell opens the locked column
                                                    // (its own picture, quick look); a VEILED cell
                                                    // has no picture to show at all, so its door
                                                    // still goes straight to sign-in, unchanged. The
                                                    // `in` check (not the `open` local, which is a
                                                    // plain boolean and narrows nothing) is what lets
                                                    // TypeScript narrow `anon` for `setVisitorLocked`.
                                                    // The tap is a touch too: it orders the visitor's wall unseen (the route
                                                    // answers a closed house's visitor no key) and rides the session to sign-in.
                                                    onArm={() => {
                                                        touch(anon.id)
                                                        if ('image_key' in anon) setVisitorLocked(anon)
                                                        else openVisitorSignIn(anon.id)
                                                    }}
                                                />
                                            </div>
                                        )
                                    }

                                    if (cell.kind === 'pinned') {
                                        // A pinned cell is a VIEW of one specific take, never the
                                        // chain's own face — its image and label read off THAT take
                                        // directly, never off `chains.get(...)`'s newest (BRIEF
                                        // Iteration II §4). Tapping it only arms, at that take's own
                                        // step — no cell offers any other gesture (`Tile.tsx`'s own
                                        // header doc).
                                        const take = cell.take
                                        const tile = take.root_tile_id !== null
                                            ? (take.root ?? tileById(take.root_tile_id))
                                            : null
                                        const pinnedTarget: Armed = take.root_tile_id !== null
                                            ? {kind: 'tile', tileId: take.root_tile_id}
                                            : {kind: 'scratch', rootId: take.root_id}
                                        const base = tile ? tileLabel(tile) : (take.direction || theme.verbs.blankBasisLabel)
                                        return (
                                            <div key={cell.key} style={{width}}>
                                                <Tile
                                                    imageUrl={take.image_key ? theme.takeImageUrl(take.image_key) : null}
                                                    label={base}
                                                    words={captionDoors(tile, take.direction, pills)}
                                                    onWordTap={(door) => onHoverWordTap(door, take.root_tile_id)}
                                                    labelHref={tile ? theme.tileLabelHref(tile.pack_slug) : null}
                                                    pageLinkLabel={tile?.title}
                                                    state="done"
                                                    width={cell.width}
                                                    height={cell.height}
                                                    eager={rowIndex === 0 || measured}
                                                    onArm={() => arm(pinnedTarget, 'tap', '', {stepId: take.id})}
                                                />
                                            </div>
                                        )
                                    }

                                    const target: Armed =
                                        cell.kind === 'tile'
                                            ? {kind: 'tile', tileId: cell.tile.id}
                                            : {kind: 'scratch', rootId: cell.take.root_id}
                                    const key =
                                        cell.kind === 'tile'
                                            ? tileChainKey(cell.tile.id)
                                            : scratchChainKey(cell.take.root_id)
                                    const chain = chains.get(key)
                                    const working = !!chain?.running || firing.has(key)
                                    const newest = chain?.newestDone ?? (cell.kind === 'scratch' ? cell.take : null)
                                    const takeUrl = newest?.image_key ? theme.takeImageUrl(newest.image_key) : null
                                    // The cell at work's two paint inputs (`Tile.tsx`'s own header doc) — both
                                    // null while merely `firing` (optimistically working before `mine` has even
                                    // shown the row yet), which the cell paints as the breath alone. A
                                    // `rendering` row is place 1 by construction (`queuedOrder` above).
                                    const queuePlace = chain?.running?.status === 'queued'
                                        ? (queuedOrder.get(chain.running.id) ?? null)
                                        : chain?.running?.status === 'rendering' ? 1 : null
                                    const previewFrame = chain?.running?.status === 'rendering'
                                        ? (previewFrames.get(chain.running.id) ?? null)
                                        : null

                                    return (
                                        <div key={cell.key} style={{width}}>
                                            <Tile
                                                imageUrl={
                                                    cell.kind === 'tile'
                                                        ? (takeUrl ?? theme.tileImageUrl(cell.tile.image_key))
                                                        : takeUrl
                                                }
                                                label={
                                                    cell.kind === 'tile'
                                                        ? tileLabel(cell.tile)
                                                        : newest?.direction || theme.verbs.blankBasisLabel
                                                }
                                                words={
                                                    cell.kind === 'tile'
                                                        ? captionDoors(cell.tile, null, pills)
                                                        : captionDoors(null, newest?.direction, pills)
                                                }
                                                onWordTap={(door) => onHoverWordTap(door, cell.kind === 'tile' ? cell.tile.id : null)}
                                                labelHref={cell.kind === 'tile' ? theme.tileLabelHref(cell.tile.pack_slug) : null}
                                                pageLinkLabel={cell.kind === 'tile' ? cell.tile.title : undefined}
                                                // No cell carries a generation verb (decided 2026-09-01 for a
                                                // wall_tiles cell, extended to a from-scratch cell on the
                                                // operator's second look, 2026-09-19): tapping a cell only
                                                // arms — the bar's own Generate is the only thing that spends.
                                                state={working ? 'working' : newest ? 'done' : 'rest'}
                                                queuePlace={queuePlace}
                                                previewFrame={previewFrame}
                                                width={cell.width}
                                                height={cell.height}
                                                eager={rowIndex === 0 || measured}
                                                onArm={() => arm(target, 'tap')}
                                            />
                                        </div>
                                    )
                                })}
                            </div>
                        )
                    })
                })()}
                <div ref={sentinelRef} className={styles.sentinel} />
            </div>

            {typeof footer === 'function' ? footer(q, () => openVisitorSignIn(null)) : footer}

            {overlayOpen && armedView && !sheetSurface && (
                <Column
                    theme={theme}
                    rootLabel={armedView.rootLabel}
                    rootDoors={armedView.rootDoors}
                    onWordTap={onCaptionWordTap}
                    rootHref={armedView.rootHref}
                    resultUrl={armedView.resultUrl}
                    working={armedView.working}
                    queuePlace={armedView.queuePlace}
                    previewFrame={armedView.previewFrame}
                    isScratch={armedView.isScratch}
                    direction={direction}
                    onDirectionChange={setDirection}
                    ratio={armedView.ratio}
                    onRatioChange={setRatioPick}
                    onGenerate={handleGenerate}
                    onClose={returnToWall}
                    verbState={verbState}
                    steps={armedView.steps}
                    selectedStepId={armedView.displayed?.id ?? null}
                    onSelectStep={setSelectedStepId}
                    aspect={armedView.aspect}
                    viewport={{width: viewportW, height: viewportH}}
                    note={armedNote}
                    trapFocus={!signInOpen && !intakeOpen}
                    consentSlot={gate.open ? renderConsentCard(gate.cardProps) : null}
                    onFoldConsent={gate.fold}
                    pin={pinDoor}
                    share={shareDoor}
                    pinSlot={pinSlotState}
                    identity={identity}
                    onIdentityChange={setIdentity}
                    identityOpenSignal={identityOpenSignal}
                    onOpenIntake={openIntake}
                    allowanceBar={allowanceBar}
                />
            )}

            {overlayOpen && armedView && sheetSurface && (
                <Sheet
                    theme={theme}
                    rootLabel={armedView.rootLabel}
                    rootDoors={armedView.rootDoors}
                    onWordTap={onCaptionWordTap}
                    rootHref={armedView.rootHref}
                    resultUrl={armedView.resultUrl}
                    working={armedView.working}
                    queuePlace={armedView.queuePlace}
                    previewFrame={armedView.previewFrame}
                    isScratch={armedView.isScratch}
                    direction={direction}
                    onDirectionChange={setDirection}
                    ratio={armedView.ratio}
                    onRatioChange={setRatioPick}
                    onGenerate={handleGenerate}
                    onClose={returnToWall}
                    verbState={verbState}
                    steps={armedView.steps}
                    selectedStepId={armedView.displayed?.id ?? null}
                    onSelectStep={setSelectedStepId}
                    aspect={armedView.aspect}
                    viewport={{width: viewportW, height: viewportH}}
                    note={armedNote}
                    trapFocus={!signInOpen && !intakeOpen}
                    consentSlot={gate.open ? renderConsentCard(gate.cardProps) : null}
                    onFoldConsent={gate.fold}
                    pin={pinDoor}
                    share={shareDoor}
                    pinSlot={pinSlotState}
                    identity={identity}
                    onIdentityChange={setIdentity}
                    identityOpenSignal={identityOpenSignal}
                    onOpenIntake={openIntake}
                    allowanceBar={allowanceBar}
                />
            )}

            {/*
             * A visitor's own quick look (plan `2026_09_18_ovid_wall_polish.md` § P5) — the SAME
             * `Column`/`Sheet` a member arms, fed a minimal prop set rather than an `armedView`:
             * no `identity`/`onIdentityChange` (so the bar's own identity slot renders nothing —
             * "no identity slot"), `steps={[]}` (so `Counter` renders nothing — "no counter",
             * `Counter.tsx`'s own `total < 2` gate), `hideFrom` (no estate text to name — `AnonTile`
             * carries none), and `locked` (the pencil is a second door to sign-in, not a field
             * opener). `rootLabel` is read only for the dialog's own `aria-label`, never shown.
             * `onGenerate`/`onClose` both reach `openVisitorSignIn`/`returnToWall` — the same wire
             * and the same return token a member's `arm()` path builds.
             */}
            {visitorLocked && !sheetSurface && (
                <Column
                    theme={theme}
                    rootLabel="Picture"
                    rootDoors={[]}
                    onWordTap={() => {}}
                    rootHref={null}
                    resultUrl={theme.tileImageUrl(visitorLocked.image_key)}
                    working={false}
                    isScratch={false}
                    direction=""
                    onDirectionChange={() => {}}
                    ratio={theme.defaultRatio}
                    onRatioChange={() => {}}
                    onGenerate={() => openVisitorSignIn(visitorLocked.id, true)}
                    onClose={returnToWall}
                    verbState="generate"
                    steps={[]}
                    selectedStepId={null}
                    onSelectStep={() => {}}
                    aspect={{width: visitorLocked.width, height: visitorLocked.height}}
                    viewport={{width: viewportW, height: viewportH}}
                    trapFocus={!signInOpen}
                    locked
                    hideFrom
                />
            )}

            {visitorLocked && sheetSurface && (
                <Sheet
                    theme={theme}
                    rootLabel="Picture"
                    rootDoors={[]}
                    onWordTap={() => {}}
                    rootHref={null}
                    resultUrl={theme.tileImageUrl(visitorLocked.image_key)}
                    working={false}
                    isScratch={false}
                    direction=""
                    onDirectionChange={() => {}}
                    ratio={theme.defaultRatio}
                    onRatioChange={() => {}}
                    onGenerate={() => openVisitorSignIn(visitorLocked.id, true)}
                    onClose={returnToWall}
                    verbState="generate"
                    steps={[]}
                    selectedStepId={null}
                    onSelectStep={() => {}}
                    aspect={{width: visitorLocked.width, height: visitorLocked.height}}
                    viewport={{width: viewportW, height: viewportH}}
                    trapFocus={!signInOpen}
                    locked
                    hideFrom
                />
            )}

            {showPill && pill && (
                <Pill
                    rootLabel={pill.rootLabel}
                    slotUrl={pill.slotUrl}
                    slotWidth={pill.slotWidth}
                    slotHeight={pill.slotHeight}
                    working={pill.working}
                    ready={pill.ready}
                    line={pill.line}
                    live={pill.live}
                    onContinue={handlePillContinue}
                    onDismiss={dismissPill}
                />
            )}

            {/* The model-onboarding overlay, under the sign-in layer. No celebration, no toast, no
                route change (operator, 2026-09-22): closing drops back to the same armed tile. */}
            {intakeOpen && renderIdentityIntake && (
                <div
                    role="dialog"
                    aria-modal="true"
                    aria-label={chromeWords(theme).intake}
                    tabIndex={-1}
                    style={{
                        outline: 'none', position: 'fixed', inset: 0, zIndex: 'var(--tl-z-arena)',
                    } as React.CSSProperties}
                    onKeyDown={(e) => {
                        if (e.key === 'Escape') setIntakeOpen(false)
                    }}
                >
                    {renderIdentityIntake({
                        reopenPath: armed?.kind === 'tile' ? `/?arm=${armed.tileId}&intake=1` : null,
                        onClose: (result) => {
                            setIntakeOpen(false)
                            // Narcis's identity turns to training at once; Ovid's muses live in the strip, which reads them itself and
                            // needs the bar to be up.
                            if (result?.queued && identity.kind === 'model') {
                                setIdentity({kind: 'model', state: 'training', eta_ms: null, percent: null, thumb_key: null, name: null})
                            }
                            const next = result?.queued ? armAfterQueuedMuse(identity.kind, armed) : null
                            if (next) arm(next, 'page')
                            refetchMine()
                        },
                    })}
                </div>
            )}

            {/* `tabIndex={-1}` only so the layer itself can take focus on open (see the trap
                above); it is never in the Tab cycle, so it carries no ring of its own. */}
            {signInOpen && (
                <div
                    ref={signInRef}
                    role="dialog"
                    aria-modal="true"
                    aria-label="Sign in"
                    tabIndex={-1}
                    style={{outline: 'none'}}
                >
                    {renderSignIn({
                        redirectTo: gateRedirect,
                        trigger: gateTrigger,
                        hasPicture: !signedIn && mine !== null,
                        onClose: () => setSignInOpen(false),
                    })}
                </div>
            )}
        </div>
    )
}
